//#region Imports
import { useState, useEffect, Fragment, useRef, useMemo } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";

//oneFront SDK
import {
	useSnackbar,
	useQuery,
	useMutation,
	useTranslation,
} from "@1f/react-sdk";

//#region Vapor Components
import VaporPage from "@vapor/react-custom/VaporPage";
import VaporToolbar from "@vapor/react-custom/VaporToolbar";
import Stack from "@vapor/react-material/Stack";
import Divider from "@vapor/react-material/Divider";
import Button from "@vapor/react-material/Button";
//#endregion

//Icons
import {
	Add as AddIcon,
	SaveOutlined as SaveIcon,
	CloseOutlined as CancelIcon,
} from "@mui/icons-material";

//#region Custom components
import { Loading } from "../Loading";
import { NotAuth } from "../NotAuth";
import { ModalDelete } from "../ModalDelete";
import { ModalConfirmSave } from "../ModalConfirmSave";
import { ModalPrivateAlert } from "../ModalPrivateAlert";
import { AnagraficaSidebar } from "./AnagraficaSidebar";
import { AnagraficaVersionBar } from "./AnagraficaVersionBar";
import { AnagraficaSearchBar } from "./AnagraficaSearchBar";
import { AnagraficaTipoSection } from "./AnagraficaTipoSection";
import { AnagraficaIdentificazioneSection } from "./AnagraficaIdentificazioneSection";
import { AnagraficaGeneralitaSection } from "./AnagraficaGeneralitaSection";
import { AnagraficaAtecoSection } from "./AnagraficaAtecoSection";
import { AnagraficaIndirizziSection } from "./AnagraficaIndirizziSection";
import { AnagraficaPepSection } from "./AnagraficaPepSection";
import { AnagraficaPartecipazioneSection } from "./AnagraficaPartecipazioneSection";
import { AnagraficaRedditoPatrimonioSection } from "./AnagraficaRedditoPatrimonioSection";
import { HistoryModal } from "./HistoryModal";
import { IdentModal } from "./IdentModal";
//#endregion

//#region Methods
import {
	getDataFromFiscalCode,
	isFiscalCodeValid,
	isAFiscalCode,
} from "../../businessLogic/helpers/anagraficaHelpers";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import {
	getLocalStorageSelectedEntity,
	removeLocalStorageSelectedEntity,
} from "../../businessLogic/helpers/storageHelpers";
//#endregion

import { bsaAnagrafica } from "../../businessLogic/constants/securityAreas";
import {
	TIPO_PARTECIPAZIONE_ALTRO_KEY,
	TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
	TIPO_ANAGRAFICA_CLIENTE_PG_KEY,
	TIPO_ANAGRAFICA_LEGALE_RAPP_KEY,
	TIPO_ANAGRAFICA_TITOLARE_EFFETTIVO_KEY,
} from "../../businessLogic/constants/baseTableKeys";

//#region DataModels
import {
	createAnagraficaModel,
	createAnagraficaFascicoloModel,
	createAnagraficaBancaDatiModel,
	getEmptyAnagraficaModel,
	getEmptyAnagraficaFascicoloModel,
	createBaseComuneModel,
} from "../../businessLogic/helpers/modelHelpers";

import {
	userPermissionModel,
	anagraficaModel,
	anagraficaFascicoloModel,
} from "../../businessLogic/models";
//#endregion

//#region Query And Mutations
import {
	query_base_all,
	query_base_comune,
} from "../../businessLogic/query/baseTablesQueries";
import { query_anagrafiche_byFascicolo } from "../../businessLogic/query/anagraficaQueries";
import { query_anagrafiche_bancaDati_byAnag } from "../../businessLogic/query/bancheDatiQueries";
import { mutation_add_audit } from "../../businessLogic/mutations/auditMutations";
import { mutation_update_PIva_anagrafica } from "../../businessLogic/mutations/anagraficaFascicoloMutations";
//#endregion

//#region Other components
import postal from "postal";
import AutoNumeric from "autonumeric";
import { useNavigate } from "react-router-dom";
import { ModalSearchResultList } from "../ModalSearchResultList";
//#endregion

//#region Api
import { UserApi } from "../../businessLogic/api/userApi";
import { AddressApi } from "../../businessLogic/api/addressApi";
import { PrestazioniApi } from "../../businessLogic/api/prestazioniApi";
import { RischiApi } from "../../businessLogic/api/rischiApi";
import { BaseTableApi } from "../../businessLogic/api/baseTableApi";
import { LogApi } from "../../businessLogic/api/logApi";
//#endregion

//#region Helpers
import { AnagraficaHelpers } from "../../businessLogic/helpers/anagraficaHelpers";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
import { AddressHelpers } from "../../businessLogic/helpers/addressHelpers";
import { BaseTableHelpers } from "../../businessLogic/helpers/baseTableHelpers";
import { EuroConferenceApi } from "../../businessLogic/api/euroconferenceApi";
import { DateHelpers } from "../../businessLogic/helpers/dateHelpers";
import { AnagraficaApi } from "../../businessLogic/api/anagraficaApi";
import { AnagraficaFascicoloApi } from "../../businessLogic/api/anagraficaFascicoloApi";
import { WorkspaceApi } from "../../businessLogic/api/workspaceApi";
import { AdeApi } from "../../businessLogic/api/AdeApi";
import { CervedApi } from "../../businessLogic/api/cervedApi";
import { getCervedProvinceDifference } from "../../businessLogic/helpers/cervedHelpers.mjs";
//#endregion

export const AnagraficaForm = ({
	fascicolo,
	reloadStatusFascicolo,
	selectedValue,
}) => {
	const tenantId = sharedDatas.getPropertyByName("tenantId");
	const aziendaId = sharedDatas.getPropertyByName("aziendaId");
	const loggedUserId = sharedDatas.getPropertyByName("userId");
	const userModel = sharedDatas.getPropertyByName("userModel");
	const environment = sharedDatas.getPropertyByName("environment");
	const resolvedAnagFromNavigation = getLocalStorageSelectedEntity();
	var tenantTypeId = userModel ? userModel.tenantTipoId : null;

	//#region Autonumeric
	const txtImportoRef = useRef();
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	//#endregion

	//#region Api
	const workspaceApi = WorkspaceApi();
	const addressApi = AddressApi();
	const baseTableApi = BaseTableApi();
	const userApi = UserApi();
	const prestazioniApi = PrestazioniApi();
	const rischiApi = RischiApi();
	const fascicoloApi = FascicoloApi();
	const euroConferenceApi = EuroConferenceApi();
	const anagraficaApi = AnagraficaApi();
	const anagraficaFascicoloApi = AnagraficaFascicoloApi();
	const logApi = LogApi();
	const adeApi = AdeApi();
	const cervedApi = CervedApi();
	//#endregion

	//#region Helpers
	const anagHelpers = AnagraficaHelpers();
	const addressHelpers = AddressHelpers();
	const baseTableHelpers = BaseTableHelpers();
	const dateHelpers = DateHelpers();
	//#endregion

	//#region Queries and Mutations
	const apiBaseComune = useQuery(
		query_base_comune,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiAnagraficaByFascicolo = useQuery(
		query_anagrafiche_byFascicolo,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiBaseAll = useQuery(
		query_base_all,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiAnagraficaBancaDati = useQuery(
		query_anagrafiche_bancaDati_byAnag,
		{},
		{ lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" },
	);
	const apiAddAudit = useMutation(mutation_add_audit, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	const apiUpdatePIvaAnagrafica = useMutation(mutation_update_PIva_anagrafica, {
		graphqlEndpoint: "aml:hasura:api://v1/graphql",
	});
	//#endregion

	//#region useState
	//TSDIGITAL
	const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
	const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
	const [userPermissions, setUserPermissions] = useState(
		new userPermissionModel(
			0,
			aziendaId,
			bsaAnagrafica,
			false,
			false,
			false,
			false,
		),
	);
	//Professionisti
	const [allUtentiList, setAllUtentiList] = useState([]);
	const [utentiList, setUtentiList] = useState([]);
	//Anagrafica
	const [, setCurrentAnagraficheList] = useState([]); //Contiene le anagrafiche con l'ultima versione
	const [currentAnagraficheHistoryList, setCurrentAnagraficheHistoryList] =
		useState([]); //Contiene tutte le anagrafiche con tutte le versioni
	const [
		currentAnagraficheHistoryFilteredList,
		setCurrentAnagraficheHistoryFilteredList,
	] = useState([]); //Contiene tutte le anagrafiche filtrate
	const [currentAnagraficheClientiList, setCurrentAnagraficheClientiList] =
		useState([]); //Contiene le anagrafiche clienti correnti
	const [currentAnagraficheCollegateList, setCurrentAnagraficheCollegateList] =
		useState([]); //Contiene le anagrafiche clienti correnti
	const [currentAnagraficheTitEffList, setCurrentAnagraficheTitEffList] =
		useState([]); //Contiene le anagrafiche clienti correnti
	const [currentAnagrafica, setCurrentAnagrafica] = useState(
		new anagraficaModel(
			0,
			"",
			"",
			"",
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			false,
			false,
			new Date(),
			1,
			new Date(),
			0,
			false,
			89,
			null,
			null,
			null,
			null,
			null,
			false,
			null,
			"",
			loggedUserId,
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			1,
			1,
			0,
			"",
			"",
			"",
			"",
			false,
			"",
			"IT",
			"",
			null,
			"",
			1,
			0,
			"",
			"",
			new Date(),
			null,
			"",
			"",
			loggedUserId,
			new Date(),
			0,
			[],
		),
	); //Contiene l'anagrafica corrente
	const [currentAnagraficaOriginal, setCurrentAnagraficaOriginal] = useState(
		new anagraficaModel(
			0,
			"",
			"",
			"",
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			false,
			false,
			new Date(),
			1,
			new Date(),
			0,
			false,
			89,
			null,
			null,
			null,
			null,
			null,
			false,
			null,
			"",
			loggedUserId,
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			1,
			1,
			0,
			"",
			"",
			"",
			"",
			false,
			"",
			"IT",
			"",
			null,
			"",
			1,
			0,
			"",
			"",
			new Date(),
			null,
			"",
			"",
			loggedUserId,
			new Date(),
			0,
			[],
		),
	); //Used for Audit
	const [anagraficheBancaDatiList, setAnagraficheBancaDatiList] = useState([]); //Contiene i risultati delle banche dati di tutte le anagrafiche
	const [currentAnagraficaNewVer, setCurrentAnagraficaNewVer] = useState(
		new anagraficaModel(
			0,
			"",
			"",
			"",
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			false,
			false,
			new Date(),
			1,
			new Date(),
			0,
			false,
			89,
			null,
			null,
			null,
			null,
			null,
			false,
			null,
			"",
			loggedUserId,
			"",
			new Date(),
			"",
			"",
			"",
			"",
			"",
			"",
			"",
			1,
			1,
			0,
			"",
			"",
			"",
			"",
			false,
			"",
			"IT",
			"",
			null,
			"",
			1,
			0,
			"",
			"",
			new Date(),
			null,
			"",
			"",
			loggedUserId,
			new Date(),
			0,
			[],
		),
	); //Contiene l'anagrafica corrente
	const [currentAnagraficaFascicolo, setCurrentAnagraficaFascicolo] = useState(
		new anagraficaFascicoloModel(0, 0, 0, 0, 0, "", null, 0, ""),
	);
	//Tabelle di base
	const [baseTipoAnagList, setBaseTipoAnagList] = useState([]); //base_tipo_anagrafica
	const [baseGenereList, setBaseGenereList] = useState([]); //base_genere
	const [baseTipoDocList, setBaseTipoDocList] = useState([]); //base_tipo_documento
	const [baseTipoIdentList, setBaseTipoIdentList] = useState([]); //base_tipo_identificazione
	const [basePaeseList, setBasePaeseList] = useState([]); //base_paese
	const [baseProvinciaList, setBaseProvinciaList] = useState([]); //base_provincia
	const [baseComuneList, setBaseComuneList] = useState([]); //base_comune
	const [baseComuneList2, setBaseComuneList2] = useState([]); //base_comune
	const [baseTipoPartList, setBaseTipoPartList] = useState([]); //base_tipo_partecipazione
	const [baseTipoRappList, setBaseTipoRappList] = useState([]); //base_tipo_rappresentanza
	const [baseTipoPatrimonio, setBaseTipoPatrimonio] = useState([]); //base_tipo_patrimonio
	const [baseTipoReddito, setBaseTipoReddito] = useState([]); //base_tipo_reddito
	const [basePoliticalRole, setBasePoliticalRole] = useState([]); //base ruoli politici (ppe)
	const [baseAtecoList, setBaseAtecoList] = useState([]); //base_ateco
	//Valori form
	const [tipoAnag, setTipoAnag] = useState("cliente");
	const [isPG, setIsPG] = useState(false);
	const [isAddrPaeseIta, setIsAddrPaeseIta] = useState(true);
	const [isAddr2PaeseIta, setIsAddr2PaeseIta] = useState(true);
	const [, setIsModIdentDiretta] = useState(false);
	const [anagPep, setAnagPep] = useState({
		IsNotPep: true,
		IsPep: false,
		IsNotPepMoreThanYear: false,
	});
	const [showDocTipoAp, setShowDoctipoAp] = useState(false);
	const [showDocTipoDetails, setShowDocTipoDetails] = useState(false);
	const isModIdentArt22 = useMemo(() => {
		if (!baseTipoIdentList.length || !currentAnagrafica.modalitaIdentId)
			return false;
		const key = baseTipoIdentList.find(
			(x) => x.id === currentAnagrafica.modalitaIdentId,
		)?.key;
		return key === "tipo_identificazione_2" && tipoAnag === "titolare";
	}, [currentAnagrafica.modalitaIdentId, tipoAnag, baseTipoIdentList]);
	const [enableFormControls, setEnableFormControls] = useState(true);
	const [isVersionSavingDisabled, setIsVersionSavingDisabled] = useState(true);
	const [showTipoPartDesc, setShowTipoPartDesc] = useState(false);
	const [enableTipoRappDesc, setEnableTipoRappDesc] = useState(
		currentAnagraficaFascicolo.tipoRappId === 2,
	);
	const [, setIsDataFascicoloEnabled] = useState(true);
	const [isUserAllowedToPrivate, setIsUserAllowedToPrivate] = useState(false);
	const [isCessato, setIsCessato] = useState(false); //Identifica se l'anagrafica è cessata
	const [isNotLastVersion, setIsNotLastVersion] = useState(false); //Identifica se l'anagrafica è l'ultima versione
	const [showDeleteModal, setShowDeleteModal] = useState(false); //Modal per la conferma di eliminazione di un record
	const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false); //Modal per la conferma di salvataggio standard nel caso i dati anagrafici siano sballati con quelli ricavati dal codice fiscale
	const [showSaveConfirmModalVer, setShowSaveConfirmModalVer] = useState(false); //Modal per la conferma di salvataggio versione nel caso i dati anagrafici siano sballati con quelli ricavati dal codice fiscale
	const [showHistoryModal, setShowHistoryModal] = useState(false);
	const [showIdentModal, setShowIdentModal] = useState(false);
	const [showPrivateConfirmModal, setShowPrivateConfirmModal] = useState(false); //Modal per conferma cambio stato privato fascicolo
	const [pendingPrivateValue, setPendingPrivateValue] = useState(false); //Valore pendente del checkbox privato
	const [showOtherPatrimonio, setShowOtherPatrimonio] = useState(false);
	const [showOtherReddito, setShowOtherReddito] = useState(false);
	const [showResultListModal, setShowResultListModal] = useState(false);
	const [resultList, setResultList] = useState([]);
	const [idToPerform, setIdToPerform] = useState(0); //Contiene l'id del record da visualizzare
	const [typeToPerform, setTypeToPerform] = useState("P"); //Contiene il tipo di record da eliminare. A: Anagrafica, ALL: Allegato
	const [anagFascToPerform, setAnagFascToPerform] = useState(null);
	const [lastSelectedAnag, setLastSelectedAnag] = useState(null);
	const [showAnagForm, setShowAnagForm] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [isPIvaChanged, setIsPIvaChanged] = useState(false);
	const [clienteAnagType, setClienteAnagType] = useState(selectedValue || "PG");
	const [wasIdentifiedOnLoad, setWasIdentifiedOnLoad] = useState(true); // Traccia se l'anagrafica era identificata al caricamento
	const [differences, setDifferences] = useState({});
	const cervedComparisonRequestRef = useRef(0);
	//#endregion

	//#region Effects
	useEffect(() => {
		loadDatas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataLoaded]);
	// eslint-disable-next-line no-unused-vars
	let txtImportoAutoNum = null;
	useEffect(() => {
		if (txtImportoRef.current !== undefined && txtImportoRef.current !== null) {
			const txtImportoEl = txtImportoRef.current.children[1].children[0];
			// eslint-disable-next-line react-hooks/exhaustive-deps
			txtImportoAutoNum = new AutoNumeric(txtImportoEl, {
				decimalCharacter: ",",
				digitGroupSeparator: ".",
				decimalPlaces: 2,
			});
		}
	}, [currentAnagrafica]);
	//#endregion
	async function loadDatas() {
		setBackDropOpen(true);
		try {
			if (isDataLoaded) return;
			// 2. POI carica permessi e tabelle base
			setUserPermissions(
				sharedDatas
					.getPropertyByName("userPermissions")
					.find((x) => x.area === bsaAnagrafica),
			);
			const baseTables = await loadAllBaseTables();
			await loadUsers();
			// 3. Carica anagrafiche se il fascicolo è valido
			if (fascicolo.id <= 0) {
				return;
			}
			let anagrafiche = [];
			try {
				anagrafiche = await anagraficaApi.GetAnagraficheByFascicoloId(
					fascicolo.id,
				);
			} catch (error) {
				logApi.AddLog(
					error.message,
					error.stack,
					"loadDatas/GetAnagraficheByFascicoloId",
					"AnagraficaForm",
				);
				enqueueSnackbar(error, { variant: "error" });
				return;
			}
			// Se nessuna anagrafica, mostra warning e prepara eliminazione fascicolo
			if (anagrafiche.length === 0) {
				enqueueSnackbar(
					"Ops... questo fascicolo non ha un' anagrafica cliente, procedi all'eliminazione",
					{ variant: "warning" },
				);
				setTypeToPerform("F");
				setIdToPerform(fascicolo.id);
				setShowDeleteModal(true);
				return;
			}
			// 4. Prepara liste anagrafiche e banche dati
			const {
				allAnagrafiche,
				allVersioni,
				anagClienti,
				anagCollegate,
				anagTitEff,
				bancheDati,
			} = await processAnagrafiche(anagrafiche, baseTables, fascicolo.id);
			setCurrentAnagraficheList(allAnagrafiche);
			setCurrentAnagraficheHistoryList(allVersioni);
			setCurrentAnagraficheClientiList(anagClienti);
			setCurrentAnagraficheCollegateList(anagCollegate);
			setCurrentAnagraficheTitEffList(anagTitEff);
			setAnagraficheBancaDatiList(bancheDati);
			if (anagClienti.length > 0) {
				setClienteAnagType(anagHelpers.GetTipoCliente(anagClienti[0]));
			}
			// 5. Seleziona anagrafica da visualizzare CON IL VALORE GIÀ RISOLTO
			await selectDefaultAnagrafica({
				allAnagrafiche,
				allVersioni,
				anagClienti,
				anagCollegate,
				anagTitEff,
				baseTables,
				resolvedAnagFromNavigation,
				lastSelectedAnag,
				currentTipoAnag: tipoAnag,
				emptyAnagraficaModel,
				emptyAnagraficaFascicoloModel,
				removeLocalStorageSelectedEntity,
				selectAnagrafica,
				setCurrentAnagrafica,
				setCurrentAnagraficaOriginal,
				setCurrentAnagraficaFascicolo,
				setLastSelectedAnag,
			});
			// 6. Abilita/disabilita data fascicolo in base a prestazioni e rischi
			await updateFascicoloStatus();
			// 7. Pubblica lista anagrafiche e banche dati tramite postal
			publishAnagrafichePostal(allAnagrafiche, bancheDati);
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "LoadDatas", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
			setIsDataLoaded(true);
		}
	}
	async function processAnagrafiche(anagrafiche, baseTables, fascicoloId) {
		const allAnagrafiche = [];
		const allVersioni = [];
		const anagClienti = [];
		const anagCollegate = [];
		const anagTitEff = [];
		let bancheDati = [];
		const allCf = [];
		const allAnagraficaFascicolo = [];

		for (const anagrafica of anagrafiche) {
			let anagraficaModel = createAnagraficaModel(anagrafica);
			let anagFascicolo =
				await anagraficaApi.GetAnagFascicolosFromFascicoloId(fascicoloId);
			anagraficaModel.anagraficaFascicoli = anagFascicolo
				.filter((p) => p.anagraficaId === anagraficaModel.id)
				.map((anagraficaFascicolo) =>
					createAnagraficaFascicoloModel(
						anagraficaFascicolo,
						fascicoloId,
						anagraficaModel.id,
					),
				);
			let allVersionResponse = await anagraficaApi.GetAllAnagraficaVersions(
				anagraficaModel.id,
			);
			allVersionResponse.forEach((av) => {
				allVersioni.push(createAnagraficaModel(av));
			});
			allVersioni.push(anagraficaModel);
			allAnagrafiche.push(anagraficaModel);

			bancheDati = [...bancheDati, ...(await loadBancheDati(anagraficaModel))];

			for (const anagraficaFascicolo of anagraficaModel.anagraficaFascicoli) {
				const tipoKey = baseTables.arrTipoAnagItems.find(
					(bta) => bta.id === anagraficaFascicolo.tipoAnagId,
				).key;
				const newAnagraficaModel = { ...anagraficaModel };
				newAnagraficaModel.anagraficaFascicoli = [
					...newAnagraficaModel.anagraficaFascicoli,
				];

				if (
					anagHelpers.IsAnagraficaCliente(tipoKey) &&
					!anagHelpers.AnagraficaAlreadyInCollection(
						anagClienti,
						newAnagraficaModel.cf,
					)
				) {
					anagClienti.push(newAnagraficaModel);
				} else if (
					anagHelpers.IsAnagraficaCollegata(tipoKey) &&
					!anagHelpers.AnagraficaAlreadyInCollection(
						anagCollegate,
						newAnagraficaModel.cf,
					)
				) {
					anagCollegate.push(newAnagraficaModel);
				} else if (
					anagHelpers.IsAnagraficaTitolareEffettivo(tipoKey) &&
					!anagHelpers.AnagraficaAlreadyInCollection(
						anagTitEff,
						newAnagraficaModel.cf,
					)
				) {
					anagTitEff.push(newAnagraficaModel);
				}

				allCf.push(anagraficaModel);
				allAnagraficaFascicolo.push(anagraficaFascicolo);
			}
		}

		return {
			allAnagrafiche,
			allVersioni,
			anagClienti,
			anagCollegate,
			anagTitEff,
			bancheDati,
		};
	}
	async function selectDefaultAnagrafica({
		allAnagrafiche,
		allVersioni,
		anagClienti,
		anagCollegate,
		anagTitEff,
		baseTables,
		resolvedAnagFromNavigation, // <-- Ora è il valore già risolto
		lastSelectedAnag,
		currentTipoAnag,
		emptyAnagraficaModel,
		emptyAnagraficaFascicoloModel,
		removeLocalStorageSelectedEntity,
		selectAnagrafica,
		setCurrentAnagrafica,
		setCurrentAnagraficaOriginal,
		setCurrentAnagraficaFascicolo,
		setLastSelectedAnag,
	}) {
		let historyItems;

		// CORREZIONE: Usa la variabile corretta
		const anagFromNavigationResultJson = resolvedAnagFromNavigation
			? typeof resolvedAnagFromNavigation === "string"
				? JSON.parse(resolvedAnagFromNavigation)
				: resolvedAnagFromNavigation
			: null;

		console.log(
			"Processing anagFromNavigationResultJson:",
			anagFromNavigationResultJson,
		); // Debug

		if (!lastSelectedAnag) {
			if (
				anagFromNavigationResultJson &&
				anagFromNavigationResultJson.entityType === "anagrafica" &&
				anagFromNavigationResultJson.entityId
			) {
				// Il match deve essere resiliente ai diversi "spazi di id" introdotti dal
				// versioning delle anagrafiche: l'id salvato in navigazione (da getDossiers)
				// puo' non coincidere con l'id del modello caricato via getAnagraficaFromDB.
				// Si prova prima l'uguaglianza esatta, poi per coercizione di tipo, infine
				// per anagraficaId/parentId (coerente con getHistoryItems).
				const targetId = anagFromNavigationResultJson.entityId;
				const itemToSelect =
					allAnagrafiche.find((x) => x.id === targetId) ||
					allAnagrafiche.find((x) => String(x.id) === String(targetId)) ||
					allAnagrafiche.find(
						(x) => x.anagraficaId === targetId || x.parentId === targetId,
					);

				if (itemToSelect) {
					historyItems = getHistoryItems(itemToSelect, allVersioni);
					// Se il chiamante ha specificato il tipo (options) lo usiamo; altrimenti
					// (es. navigazione dalla Dashboard) lo deduciamo dal ruolo effettivo
					// dell'anagrafica trovata, cosi' viene aperta la sotto-scheda giusta
					// (cliente / collegata / titolare) e non sempre "cliente".
					let tipoAnag;
					if (anagFromNavigationResultJson.options) {
						tipoAnag =
							anagHelpers.GetTipoAnagFromOptions(
								anagFromNavigationResultJson.options,
							) || "cliente";
					} else if (
						(anagClienti || []).some((x) => x.id === itemToSelect.id)
					) {
						tipoAnag = "cliente";
					} else if (
						(anagCollegate || []).some((x) => x.id === itemToSelect.id)
					) {
						tipoAnag = "collegate";
					} else if ((anagTitEff || []).some((x) => x.id === itemToSelect.id)) {
						tipoAnag = "titolare";
					} else {
						tipoAnag = "cliente";
					}

					removeLocalStorageSelectedEntity();
					setLastSelectedAnag(itemToSelect);
					await selectAnagrafica(
						itemToSelect,
						baseTables.arrtipoIdenItems,
						baseTables.arrTipoDocItems,
						baseTables.arrPaeseItems,
						baseTables.arrProvItems,
						baseTables.arrTipoAnagItems,
						baseTables.arrTipoPartItems,
						historyItems,
						tipoAnag,
					);
					return; // IMPORTANTE: esci dalla funzione
				} else {
					console.warn(
						"Anagrafica non trovata con ID:",
						anagFromNavigationResultJson.entityId,
					);
				}
			}

			// Fallback: seleziona il primo cliente
			if (anagClienti.length > 0) {
				setLastSelectedAnag(anagClienti[0]);
				historyItems = getHistoryItems(anagClienti[0], allVersioni);
				await selectAnagrafica(
					anagClienti[0],
					baseTables.arrtipoIdenItems,
					baseTables.arrTipoDocItems,
					baseTables.arrPaeseItems,
					baseTables.arrProvItems,
					baseTables.arrTipoAnagItems,
					baseTables.arrTipoPartItems,
					historyItems,
					"cliente",
				);
			} else {
				const newItem = await emptyAnagraficaModel(baseTables.arrPaeseItems);
				setCurrentAnagrafica(newItem);
				setCurrentAnagraficaOriginal(newItem);
				setCurrentAnagraficaFascicolo(emptyAnagraficaFascicoloModel());
			}
		} else {
			historyItems = getHistoryItems(lastSelectedAnag, allVersioni);
			const tipoAnag = currentTipoAnag || "cliente";
			await selectAnagrafica(
				lastSelectedAnag,
				baseTables.arrtipoIdenItems,
				baseTables.arrTipoDocItems,
				baseTables.arrPaeseItems,
				baseTables.arrProvItems,
				baseTables.arrTipoAnagItems,
				baseTables.arrTipoPartItems,
				historyItems,
				tipoAnag,
			);
		}
	}
	function getHistoryItems(anag, allVersioni) {
		let items = allVersioni
			.filter((x) => x.id === anag.id || x.anagraficaId === anag.id)
			.sort((a, b) => b.versionN - a.versionN);
		return items;
	}
	async function updateFascicoloStatus() {
		const prestByFascicolo = await prestazioniApi.GetPrestazioniByFascicoloId(
			fascicolo.id,
		);
		const countPrest = prestByFascicolo.length;
		let countRischi = 0;
		let countIdent = 0;

		if (countPrest > 0) {
			for (const itemP of prestByFascicolo) {
				const countRischiByPrest =
					await rischiApi.GetCountRischiByPrestazioneId(itemP.Id);
				countRischi += countRischiByPrest;

				if (
					!isUserAllowedToPrivate &&
					itemP.prestazione_users.some((x) => x.UserId === loggedUserId)
				) {
					setIsUserAllowedToPrivate(true);
				}
			}
		}

		setIsDataFascicoloEnabled(
			countIdent > 0 || countPrest > 0 || countRischi > 0,
		);
	}
	function publishAnagrafichePostal(allAnagrafiche, bancheDati) {
		const distinctAnagList = [];
		for (const anag of allAnagrafiche) {
			if (!distinctAnagList.some((x) => x.cf === anag.cf)) {
				distinctAnagList.push(anag);
			}
		}
		postal.publish({
			channel: "fascicolo",
			topic: "anagrafica.list",
			data: {
				anagrafiche: distinctAnagList,
				bancheDati: bancheDati,
			},
		});
	}
	async function loadAllBaseTables() {
		/**************************************/
		//Carico le tabelle di base
		let tbBaseAllResponse = null;
		try {
			tbBaseAllResponse = await apiBaseAll.fetch({ tenantId });

			// Verifica se la risposta contiene un errore interno
			if (!tbBaseAllResponse || tbBaseAllResponse.error) {
				throw new Error("Errore interno API: " + tbBaseAllResponse.error);
			}
		} catch (error) {
			const errorMsg = error?.message || JSON.stringify(error);
			throw new Error("Impossibile recuperare le tabelle di base: " + errorMsg);
		}

		try {
			/*base_tipo_anagrafica*/
			const arrTipoAnagItems =
				baseTableApi.GetBaseTipoAnagrafica(tbBaseAllResponse);
			setBaseTipoAnagList(arrTipoAnagItems);
			/*base_genere*/
			const arrGenereItems = baseTableApi.GetBaseGenere(tbBaseAllResponse);
			setBaseGenereList(arrGenereItems);
			/*base_tipo_identificazione*/
			const arrtipoIdenItems = baseTableApi.GetBaseTipoIdent(tbBaseAllResponse);
			setBaseTipoIdentList(arrtipoIdenItems);
			/*base_tipo_documento*/
			const arrTipoDocItems =
				baseTableApi.GetBaseTipoDocumento(tbBaseAllResponse);
			setBaseTipoDocList(arrTipoDocItems);
			// /*base_paese*/
			const arrPaeseItems = baseTableApi.GetBasePaese(tbBaseAllResponse);
			setBasePaeseList(arrPaeseItems);
			// /*base_provincia*/
			const arrProvItems = baseTableApi.GetBaseProvincia(tbBaseAllResponse);
			setBaseProvinciaList(arrProvItems);
			// /*base_tipo_partecipazione*/
			const arrTipoPartItems =
				baseTableApi.GetBaseTipoPartecipazione(tbBaseAllResponse);
			setBaseTipoPartList(arrTipoPartItems);
			// /*base_tipo_rappresentanza*/
			setBaseTipoRappList(baseTableApi.GetBaseTipoRappresentanza());
			// /*base_tipo_ruolo_politico_ricoperto*/
			setBasePoliticalRole(baseTableApi.GetBasePoliticalRole());
			// /*base_tipo_reddito*/
			const arrTipoRedditoItems =
				baseTableApi.GetBaseTipoReddito(tbBaseAllResponse);
			setBaseTipoReddito(arrTipoRedditoItems);
			// /*base_tipo_patrimonio*/
			const arrTipoPatrItems =
				baseTableApi.GetBaseTipoPatrimonio(tbBaseAllResponse);
			setBaseTipoPatrimonio(arrTipoPatrItems);
			// /*base_ateco*/
			const arrBaseAtecoItems = await baseTableApi.GetBaseAteco();
			setBaseAtecoList(arrBaseAtecoItems);

			return {
				arrTipoAnagItems,
				arrtipoIdenItems,
				arrTipoDocItems,
				arrPaeseItems,
				arrProvItems,
				arrTipoPartItems,
			};
		} catch (parseError) {
			logApi.AddLog(
				parseError.message,
				parseError.stack,
				"loadBaseTables",
				"AnagraficaForm",
			);
			throw new Error(
				"Errore nel parsing delle tabelle di base: " + parseError.message,
			);
		}
	}
	async function loadUsers() {
		/**************************************/
		//Carico gli utenti
		let { allUsers, allUserTenant } = await userApi.GetAllUsersByTenantId();
		setAllUtentiList(allUsers);
		let arrUsers = await userApi.GetUsersByCompanyId();
		arrUsers.sort((a, b) =>
			a.userId === loggedUserId ? -1 : b.userId === loggedUserId ? 1 : 0,
		);
		setUtentiList(arrUsers);
	}
	async function loadBancheDati(anagraficaModel) {
		const bancaDatiAnagResponse = await apiAnagraficaBancaDati.fetch({
			anagraficaId: anagraficaModel.id,
		});
		let anagBancheDati =
			bancaDatiAnagResponse.data.data.anagrafica_bancadati.map((bancaDati) =>
				createAnagraficaBancaDatiModel(bancaDati, anagraficaModel),
			);
		return anagBancheDati;
	}
	const loadBaseComune = async (provincia) => {
		try {
			const comuneResponse = await apiBaseComune.fetch({
				provincia: provincia,
			});
			const arrItems = [];
			comuneResponse.data.data.base_comune.map((item) =>
				arrItems.push(createBaseComuneModel(item)),
			);
			setBaseComuneList(arrItems);
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "LoadBaseComune", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	const loadBaseComune2 = async (provincia) => {
		try {
			const comuneResponse = await apiBaseComune.fetch({
				provincia: provincia,
			});
			const arrItems = [];
			comuneResponse.data.data.base_comune.map((item) =>
				arrItems.push(createBaseComuneModel(item)),
			);
			setBaseComuneList2(arrItems);
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "LoadBaseComune2", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	//Rimango in ascolto per reperire le anagrafiche di cui visualizzare le informazioni
	postal.subscribe({
		channel: "bancaDati",
		topic: "bancaDati.result",
		callback: function (data, envelope) {
			const items = data.result;

			const bancaDatiResultArr = Object.assign([], anagraficheBancaDatiList);
			for (var i = 0; i < items.length; i++) {
				bancaDatiResultArr.push(items[i]);
			}
			setAnagraficheBancaDatiList(bancaDatiResultArr);
		},
	});
	async function loadAndCompareCervedDatas(currentAnagCliente, provinceList, tipoAnagrafica = "cliente") {
		const requestId = ++cervedComparisonRequestRef.current;
		setDifferences({});
		if (tipoAnagrafica !== "cliente" || !currentAnagCliente?.isPg) return;
		try {
			const criteria = {
				codiceFiscale: currentAnagCliente?.cf || null,
				partitaIva: currentAnagCliente?.pIva || null
			};
			const cervedResponse = await cervedApi.SearchEntitiesAdvanced(criteria);
			if (requestId !== cervedComparisonRequestRef.current) return;
			if (cervedResponse?.items?.length > 0) {
				const addrComune = currentAnagCliente.addrComuneId > 0
					? await addressApi.GetComuneNomeByComuneId(currentAnagCliente.addrComuneId)
					: currentAnagCliente.addrComune;
				if (requestId !== cervedComparisonRequestRef.current) return;
				const diffs = compareCervedData(
					{ ...currentAnagCliente, addrComune },
					cervedResponse.items[0],
					provinceList,
				);
				setDifferences(diffs);
			}
		} catch (error) {
			if (requestId !== cervedComparisonRequestRef.current) return;
			logApi.AddLog(error.message, error.stack, "loadAndCompareCervedDatas", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	}
	function compareCervedData(currentAnagCliente, cervedResponse, provinceList) {
		const FIELD_MAPPING = {
			denominazione: "denominazione",
			cf: "codiceFiscale",
			pIva: "partitaIva",
			addrIndirizzo: "indirizzo",
			addrComune: "comune",
			addrProvincia: "provincia",
		};

		const normalize = (value) => {
			if (value === null || value === undefined) {
				return null;
			}
			const normalized = String(value).trim().toUpperCase();
			return normalized === "" ? null : normalized;
		};

		return Object.entries(FIELD_MAPPING).reduce(
			(diffs, [anagField, cervedField]) => {
				const currentValue = currentAnagCliente?.[anagField];
				const cervedValue = cervedResponse?.[cervedField];
				const normalizedCurrent = normalize(currentValue);
				const normalizedCerved = normalize(cervedValue);
				if (anagField === "addrComune" && normalizedCurrent === null) {
					return diffs;
				}
				const provinceDifference =
					anagField === "addrProvincia"
						? getCervedProvinceDifference(
								currentAnagCliente?.addrProvinciaId,
								currentValue,
								cervedValue,
								provinceList,
							)
						: null;
				const hasDifference = provinceDifference
					? provinceDifference.hasDifference
					: normalizedCurrent !== normalizedCerved;
				if (hasDifference) {
					diffs[anagField] = {
						currentValue: provinceDifference?.currentValue ?? currentValue,
						cervedValue,
						hasDifference: true,
					};
				}
				return diffs;
			},
			{},
		);
	}
	//#endregion

	//#region Handler
	const handleChangeFascicolo = async (event) => {
		const { id } = event.target;
		if (id === "chkPrivate") {
			// Apro la modale di conferma invece di aggiornare direttamente
			setPendingPrivateValue(event.target.checked);
			setShowPrivateConfirmModal(true);
		}
	};
	const handlePrivateConfirmClose = () => {
		setPendingPrivateValue(false);
		setShowPrivateConfirmModal(false);
	};
	const handlePrivateConfirmOk = async () => {
		try {
			setBackDropOpen(true);
			fascicolo.isPrivate = pendingPrivateValue;
			fascicolo.aziendaId = aziendaId;
			fascicolo.tenantId = tenantId;

			let fascicoloUpdated = await fascicoloApi.UpdateFascicolo(
				fascicolo,
				pendingPrivateValue,
			);
			if (fascicoloUpdated) {
				enqueueSnackbar(t("message.success"), { variant: "success" });
			}
		} catch (error) {
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setPendingPrivateValue(false);
			setShowPrivateConfirmModal(false);
			setBackDropOpen(false);
			await reloadStatusFascicolo();
		}
	};
	const handleChange = async (event, value) => {
		const { id } = event.target;
		if (id === "chbIsPg") {
			setIsPG(event.target.checked);
			currentAnagrafica.isPg = event.target.checked;

			if (!event.target.checked) {
				currentAnagrafica.tipoAnagId = baseTipoAnagList.filter(
					(item) => item.key === TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
				)[0].id;
			} else {
				currentAnagrafica.tipoAnagId = baseTipoAnagList.filter(
					(item) => item.key === TIPO_ANAGRAFICA_CLIENTE_PG_KEY,
				)[0].id;
			}
		} else if (id.indexOf("ddlTipoAnag-option") !== -1) {
			currentAnagraficaFascicolo.tipoAnagId = value.id;
		} else if (id === "txtCf") {
			currentAnagrafica.cf = event.target.value;
		} else if (id === "txtNome") {
			currentAnagrafica.nome = event.target.value;
			currentAnagrafica.denominazione =
				clienteAnagType !== "DI"
					? event.target.value + " " + currentAnagrafica.cognome
					: currentAnagrafica.denominazione;
		} else if (id === "txtCognome") {
			currentAnagrafica.cognome = event.target.value;
			currentAnagrafica.denominazione =
				clienteAnagType !== "DI"
					? currentAnagrafica.nome + " " + event.target.value
					: currentAnagrafica.denominazione;
		} else if (id.indexOf("ddlGenere-option") !== -1 || id === "ddlGenere") {
			currentAnagrafica.genereId = value?.id;
		} else if (id === "txtDenominazione") {
			currentAnagrafica.denominazione = event.target.value;
		} else if (id === "txtorgAmmin") {
			currentAnagrafica.orgAmmin = event.target.value;
		} else if (id === "txtProfessione") {
			currentAnagrafica.professione = event.target.value;
		} else if (id === "txtNatoA") {
			currentAnagrafica.nascitaLuogo = event.target.value;
		} else if (id === "txtPIva") {
			currentAnagrafica.pIva = event.target.value;
			setIsPIvaChanged(true);
		} else if (id === "txtReaComune") {
			currentAnagrafica.reaComune = event.target.value;
		} else if (id === "txtReaN") {
			currentAnagrafica.reaN = event.target.value;
		} else if (
			id.indexOf("ddlAddrPaese-option") !== -1 ||
			id === "ddlAddrPaese"
		) {
			currentAnagrafica.addrPaeseId = value.id;

			currentAnagrafica.addrProvincia = "";
			currentAnagrafica.addrComune = "";
			currentAnagrafica.addrProvinciaId = null;
			currentAnagrafica.addrComuneId = null;
			setBaseComuneList([]);

			var country = basePaeseList.filter(
				(item) => item.id === currentAnagrafica.addrPaeseId,
			)[0];

			setIsAddrPaeseIta(country.identificativo === "ITA");

			//Visualizzo il rischio
			if (country.isRischio) {
				enqueueSnackbar(t("anagrafica.message.paeseRischio"), {
					variant: "error",
					autoHideDuration: 7000,
				});
			}
		} else if (
			id.indexOf("ddlAddrProvincia-option") !== -1 ||
			id === "ddlAddrProvincia"
		) {
			currentAnagrafica.addrProvinciaId = value.id;
			currentAnagrafica.addrComune = "";
			currentAnagrafica.addrComuneId = null;

			//Recupero le città
			var provincia = baseProvinciaList.filter(
				(item) => item.id === currentAnagrafica.addrProvinciaId,
			)[0];
			loadBaseComune(provincia.sigla);

			//Visualizzo il rischio
			switch (provincia.rischio) {
				case 1: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio1"), {
						variant: "success",
					});
					break;
				}
				case 2: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio2"), {
						variant: "warning",
					});
					break;
				}
				case 3: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio3"), {
						variant: "warning",
					});
					break;
				}
				case 4: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio4"), {
						variant: "error",
					});
					break;
				}
				default: {
					break;
				}
			}
		} else if (
			id.indexOf("ddlAddrComune-option") !== -1 ||
			id === "ddlAddrComune"
		) {
			currentAnagrafica.addrComuneId = value.id;
			let comuneSelected = baseComuneList.filter(
				(item) => item.id === currentAnagrafica.addrComuneId,
			)[0];
			let cap = comuneSelected
				? await addressApi.GetCapByComuneId(comuneSelected.codBelfiore)
				: "";
			currentAnagrafica.addrCap = cap ? cap : "";
		} else if (id === "txtAddrProvincia") {
			currentAnagrafica.addrProvincia = event.target.value;
		} else if (id === "txtAddrComune") {
			currentAnagrafica.addrComune = event.target.value;
		} else if (id === "txtAddrIndirizzo") {
			currentAnagrafica.addrIndirizzo = event.target.value;
		} else if (id === "txtAddrN") {
			currentAnagrafica.addrN = event.target.value;
		} else if (id === "txtAddrCap") {
			currentAnagrafica.addrCap = event.target.value;
		} else if (
			id.indexOf("ddlAddr2Paese-option") !== -1 ||
			id === "ddlAddr2Paese"
		) {
			currentAnagrafica.addr2PaeseId = value.id;
			setBaseComuneList2([]);

			//Recupero le province
			country = basePaeseList.filter(
				(item) => item.id === currentAnagrafica.addr2PaeseId,
			)[0];
			if (country.identificativo === "ITA") {
				currentAnagrafica.addr2Provincia = "";
				currentAnagrafica.addr2Comune = "";
			} else {
				currentAnagrafica.addr2ProvinciaId = null;
				currentAnagrafica.addr2ComuneId = null;
			}

			setIsAddr2PaeseIta(country.identificativo === "ITA");

			//Visualizzo il rischio
			if (country.isRischio) {
				enqueueSnackbar(t("anagrafica.message.paeseRischio"), {
					variant: "error",
				});
			}
		} else if (
			id.indexOf("ddlAddr2Provincia-option") !== -1 ||
			id === "ddlAddr2Provincia"
		) {
			currentAnagrafica.addr2ProvinciaId = value.id;
			currentAnagrafica.addr2ComuneId = 0;

			//Recupero le città
			provincia = baseProvinciaList.filter(
				(item) => item.id === currentAnagrafica.addr2ProvinciaId,
			)[0];
			loadBaseComune2(provincia.sigla);

			//Visualizzo il rischio
			switch (provincia.rischio) {
				case 1: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio1"), {
						variant: "success",
					});
					break;
				}
				case 2: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio2"), {
						variant: "warning",
					});
					break;
				}
				case 3: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio3"), {
						variant: "warning",
					});
					break;
				}
				case 4: {
					enqueueSnackbar(t("anagrafica.message.provinciaRischio4"), {
						variant: "error",
					});
					break;
				}
				default: {
					break;
				}
			}
		} else if (
			id.indexOf("ddlAddr2Comune-option") !== -1 ||
			id === "ddlAddr2Comune"
		) {
			currentAnagrafica.addr2ComuneId = value.id;
			let comune2Selected = baseComuneList2.filter(
				(item) => item.id === currentAnagrafica.addr2ComuneId,
			)[0];
			let cap2 = comune2Selected
				? await addressApi.GetCapByComuneId(comune2Selected.codBelfiore)
				: "";
			currentAnagrafica.addr2Cap = cap2 ? cap2 : "";
		} else if (id === "txtAddr2Provincia") {
			currentAnagrafica.addr2Provincia = event.target.value;
		} else if (id === "txtAddr2Comune") {
			currentAnagrafica.addr2Comune = event.target.value;
		} else if (id === "txtAddr2Indirizzo") {
			currentAnagrafica.addr2Indirizzo = event.target.value;
		} else if (id === "txtAddr2N") {
			currentAnagrafica.addr2N = event.target.value;
		} else if (id === "txtAddr2Cap") {
			currentAnagrafica.addr2Cap = event.target.value;
		} else if (event.target.name === "radioBtnIsPep") {
			if (value === "isNotPep") {
				currentAnagrafica.isPep = false;
				currentAnagrafica.isNotPepMoreThanYear = false;
				setAnagPep((prevState) => ({
					...prevState,
					IsNotPep: true,
					IsPep: false,
					IsNotPepMoreThanYear: false,
				}));
			} else if (value === "isPep") {
				currentAnagrafica.isPep = true;
				currentAnagrafica.isNotPepMoreThanYear = false;
				setAnagPep((prevState) => ({
					...prevState,
					IsNotPep: false,
					IsPep: true,
					IsNotPepMoreThanYear: false,
				}));
			} else if (value === "isNotPepMoreThanYear") {
				currentAnagrafica.isPep = false;
				currentAnagrafica.isNotPepMoreThanYear = true;
				setAnagPep((prevState) => ({
					...prevState,
					IsNotPep: false,
					IsPep: false,
					IsNotPepMoreThanYear: true,
				}));
			}
		} else if (id === "txtPepCarica") {
			currentAnagrafica.pepCarica = event.target.value;
		} else if (
			id.indexOf("ddlPepCarica-option") !== -1 ||
			id === "ddlPepCarica"
		) {
			currentAnagrafica.pepRuolo = value;
		} else if (
			id.indexOf("ddlTipoPart-option") !== -1 ||
			id === "ddlTipoPart"
		) {
			currentAnagraficaFascicolo.tipoPartId = value.id;
			var tipoPartKey = baseTipoPartList.filter(
				(item) => item.id === value.id,
			)[0].key;
			setShowTipoPartDesc(tipoPartKey === TIPO_PARTECIPAZIONE_ALTRO_KEY);

			if (tipoPartKey !== TIPO_PARTECIPAZIONE_ALTRO_KEY) {
				currentAnagraficaFascicolo.tipoPartDesc = "";
			}
		} else if (id === "txtTipoPartDesc") {
			currentAnagraficaFascicolo.tipoPartDesc = event.target.value;
		} else if (
			id.indexOf("ddlTipoRapp-option") !== -1 ||
			id === "ddlTipoRapp"
		) {
			currentAnagraficaFascicolo.tipoRappId = value.id;
			setEnableTipoRappDesc(value.id === 2);

			if (value.id !== 2) {
				currentAnagraficaFascicolo.tipoRappDesc = "";
				setEnableTipoRappDesc(false);
			}
		} else if (id === "txtTipoRappDesc") {
			currentAnagraficaFascicolo.tipoRappDesc = event.target.value;
		} else if (
			id.indexOf("ddlPatrimonio-option") !== -1 ||
			id === "ddlPatrimonio"
		) {
			currentAnagrafica.tipoPatrimonioId = baseTipoPatrimonio.filter(
				(basePatr) => basePatr.value === value,
			)[0].id;
			currentAnagrafica.tipoPatrimonioAltro =
				baseTipoPatrimonio.filter((basePatr) => basePatr.value === value)[0]
					.id !== 5
					? ""
					: currentAnagrafica.tipoPatrimonioAltro;
			setShowOtherPatrimonio(
				baseTipoPatrimonio.filter((basePatr) => basePatr.value === value)[0]
					.id === 5,
			);
		} else if (id === "txtPatrimonioAltro") {
			currentAnagrafica.tipoPatrimonioAltro = event.target.value;
		} else if (id.indexOf("ddlReddito-option") !== -1 || id === "ddlReddito") {
			currentAnagrafica.tipoRedditoId = baseTipoReddito.filter(
				(basePatr) => basePatr.value === value,
			)[0].id;
			currentAnagrafica.tipoRedditoAltro =
				baseTipoReddito.filter((basePatr) => basePatr.value === value)[0].id !==
				6
					? ""
					: currentAnagrafica.tipoRedditoAltro;
			setShowOtherReddito(
				baseTipoReddito.filter((basePatr) => basePatr.value === value)[0].id ===
					6,
			);
		} else if (id === "txtRedditoAltro") {
			currentAnagrafica.tipoRedditoAltro = event.target.value;
		}

		//Identificazione
		else if (id.indexOf("ddlModIdent-option") !== -1 || id === "ddlModIdent") {
			const modalitaIdentKey = baseTipoIdentList.filter(
				(item) => item.id === value.id,
			)[0].key;
			setIsModIdentDiretta(modalitaIdentKey === "tipo_identificazione_1");
			setShowDoctipoAp(modalitaIdentKey === "tipo_identificazione_5");
			if (modalitaIdentKey === "tipo_identificazione_2") {
				if (currentAnagrafica.id) {
					const allFascicoli =
						await anagraficaFascicoloApi.GetAnagraficaFascicoloByAnagraficaId(
							currentAnagrafica.id,
						);
					const hasBlockingRole = allFascicoli?.some(
						(af) =>
							baseTipoAnagList.find((x) => x.id === af.TipoAnagId)?.key !==
							TIPO_ANAGRAFICA_TITOLARE_EFFETTIVO_KEY,
					);
					if (hasBlockingRole) {
						enqueueSnackbar(
							"Impossibile selezionare l'identificazione ex art. 22: questa anagrafica è presente in questo o altri fascicoli con ruoli diversi da Titolare Effettivo.",
							{ variant: "error", autoHideDuration: 7000 },
						);
						setCurrentAnagrafica({ ...currentAnagrafica });
						return;
					}
				}
				currentAnagrafica.docTipoId = null;
				currentAnagrafica.docN = "";
				currentAnagrafica.docRilDa = "";
				currentAnagrafica.docRilData = null;
				currentAnagrafica.docScad = null;
			}
			{
				currentAnagrafica.identifiedByUser =
					sharedDatas.getPropertyByName("userId");
				currentAnagrafica.modalitaIdentId = value.id;
			}
		} else if (
			id.indexOf("ddlModIdentDoc-option") !== -1 ||
			id === "ddlModIdentDoc"
		) {
			currentAnagrafica.docTipoId = value.id;
			const docTipoKey = baseTipoDocList.filter(
				(item) => item.id === currentAnagrafica.docTipoId,
			)[0].key;
			setShowDocTipoDetails(docTipoKey !== "tipo_documento_3");
		} else if (id.indexOf("ddlIdentBy-option") !== -1 || id === "ddlIdentBy") {
			currentAnagrafica.identifiedByUser = value.userId;
		} else if (id === "txtDocN") {
			currentAnagrafica.docN = event.target.value;
		} else if (id === "txtDocRilDa") {
			currentAnagrafica.docRilDa = event.target.value;
		} else if (id === "txtApNome") {
			currentAnagrafica.apNome = event.target.value;
		} else if (id === "txtApCf") {
			currentAnagrafica.apCf = event.target.value;
		} else if (
			id.indexOf("ddlAtecoCodes-option") !== -1 ||
			id === "ddlAtecoCodes"
		) {
			currentAnagrafica.atecoCode = value ? value.atecoCode : null;
			currentAnagrafica.attivitaLavDescr = value ? value.atecoTitle : null;
		} else if (
			id.indexOf("ddlAtecoCodesDesc-option") !== -1 ||
			id === "ddlAtecoCodesDesc"
		) {
			currentAnagrafica.attivitaLavDescr = value ? value.atecoTitle : null;
			currentAnagrafica.atecoCode = value ? value.atecoCode : null;
		}
		setCurrentAnagrafica({ ...currentAnagrafica });
		setCurrentAnagraficaFascicolo(currentAnagraficaFascicolo);
		setIsVersionSavingDisabled(
			anagHelpers.CompareAnagrafiche(
				currentAnagrafica,
				currentAnagraficaOriginal,
			),
		);
	};
	const handleChangeImporto = (event) => {
		const { id } = event.target;
		if (id === "txtSocialCapital") {
			let importo = parseFloat(event.target.value);
			currentAnagrafica.capSociale = importo;
		}
		setIsVersionSavingDisabled(false);
	};
	const handleBlurPIva = async (event) => {
		try {
			const { id } = event.target;
			const pIva = event.target.value;

			if (!id || !pIva || id !== "txtPIva") return;

			if (environment === "PROD") {
				let adePivaValidationResponse = await adeApi.validatePIVAFromADE(pIva);
				if (adePivaValidationResponse && !adePivaValidationResponse.valida) {
					enqueueSnackbar("P.Iva non valida", { variant: "warning" });
					setCurrentAnagrafica((prevState) => ({ ...prevState, pIva: "" }));
				}
			} else {
				let validPIva = await anagraficaApi.VerifyPIva(pIva);
				if (!validPIva) {
					enqueueSnackbar("P.Iva non valida", { variant: "warning" });
					setCurrentAnagrafica((prevState) => ({ ...prevState, pIva: "" }));
				}
			}
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "HandleBlurPIva", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	const handleBlurCF = async (event) => {
		try {
			if (!showAnagForm) return;
			const { id } = event.target;
			const cf = event.target.value;

			if (!id || id !== "txtCf" || !cf) return;

			if (environment === "PROD") {
				let adeCfValidation = await adeApi.validateCFFromADE(cf);
				if (adeCfValidation && !adeCfValidation.valido) {
					enqueueSnackbar(
						"Il codice fiscale inserito potrebbe formalmente errato o inesistente nelle banche dati ADE",
						{ variant: "warning" },
					);
					setCurrentAnagrafica((prevState) => ({ ...prevState, cf: "" }));
				}
			} else {
				if (clienteAnagType === "PF" || clienteAnagType === "DI") {
					let validCF = await anagraficaApi.VerifyCf(cf);
					if (!validCF) {
						enqueueSnackbar("Codice fiscale non valido", {
							variant: "warning",
						});
						setCurrentAnagrafica((prevState) => ({ ...prevState, cf: "" }));
					}
				} else {
					let validPIva = await anagraficaApi.VerifyPIva(cf);
					if (!validPIva) {
						enqueueSnackbar("Codice fiscale non valido", {
							variant: "warning",
						});
						setCurrentAnagrafica((prevState) => ({ ...prevState, cf: "" }));
					}
				}
			}
		} catch (error) {
			logApi.AddLog(
				error.message,
				error.stack,
				"HandleBlurCF",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	const handleSearchClick = async () => {
		try {
			setBackDropOpen(true);
			setDifferences({});
			cervedComparisonRequestRef.current += 1;

			let sesso = null;
			let codComune = null;
			let luogoNascita = "";
			let nascitaData = null;
			let genereId = null;

			if (currentAnagrafica.cf) {
				const cf = currentAnagrafica.cf.trimStart().trimEnd();
				if (!isPG && isAFiscalCode(cf)) {
					let adeResponseCf = await adeApi.validateCFFromADE(cf);
					if (
						environment === "PROD" &&
						adeResponseCf &&
						!adeResponseCf.valido
					) {
						enqueueSnackbar(
							"Il codice fiscale inserito potrebbe formalmente errato o inesistente nelle banche dati ADE",
							{ variant: "warning" },
						);
						return;
					}
					if (
						environment === "PROD"
							? (await anagraficaApi.VerifyCf(cf)) &&
								adeResponseCf &&
								adeResponseCf.valido
							: await anagraficaApi.VerifyCf(cf)
					) {
						const cfDatas = getDataFromFiscalCode(cf);
						if (cfDatas !== "") {
							nascitaData = dateHelpers.FormatToUTCWithoutTimezone(
								cfDatas.data,
								false,
							);
							sesso = cfDatas.sesso;
							codComune = cfDatas.codComune;

							let comuneResponse =
								await addressApi.GetMunicipalityByCodeBelfiore(codComune);
							if (comuneResponse) {
								luogoNascita = comuneResponse.Nome;
							}

							if (luogoNascita === "") {
								let paeseResponse =
									await addressApi.GetPaeseByCodiceBelfiore(codComune);
								if (paeseResponse) {
									luogoNascita = paeseResponse.Nome;
								}
							}

							genereId = baseGenereList.filter((x) => x.value === sesso)[0].id;
						} else if (cfDatas === "" && cf.length > 0) {
							enqueueSnackbar(
								"Il codice fiscale potrebbe non essere corretto",
								{
									variant: "warning",
								},
							);
						}
					} else {
						enqueueSnackbar("Il codice fiscale inserito non è valido", {
							variant: "warning",
						});
						setBackDropOpen(false);
						return;
					}
				}

				let foundedAnagrafica = null;

				let anagraficheFromDB = [];

				let anagResponse = await anagraficaApi.GetAnagraficaFromDB(
					cf,
					"",
					isPG,
				);
				anagResponse.forEach((anag) => {
					if (
						!anagHelpers.AnagraficaAlreadyInCollection(
							anagraficheFromDB,
							anag.cf,
						)
					) {
						anagraficheFromDB.push(anag);
					}
				});

				if (anagraficheFromDB && anagraficheFromDB.length === 1) {
					if (checkAnagAlreadyInFascicolo(anagraficheFromDB[0].cf)) {
						enqueueSnackbar(
							"L'anagrafica ricercata è già collegata al fascicolo",
							{ variant: "warning", autoHideDuration: 8000 },
						);
						setBackDropOpen(false);
						return;
					}
					foundedAnagrafica = createAnagraficaModel(anagraficheFromDB[0]);
					let anagInAzienda = !utentiList.some(
						(f) => f.userId === foundedAnagrafica.identifiedByUser,
					);
					let userName =
						allUtentiList.filter(
							(f) => f.userId === foundedAnagrafica.identifiedByUser,
						)[0]?.completeName || "";
					if (anagInAzienda && foundedAnagrafica.identifiedByUser) {
						return enqueueSnackbar(
							`L'anagrafica ricercata è stata identificata da ${userName} che non ha i permessi sul soggetto obbligato corrente`,
							{ variant: "warning", autoHideDuration: 8000 },
						);
					}
					foundedAnagrafica.anagraficaFascicoli = [
						createAnagraficaFascicoloModel(
							currentAnagraficaFascicolo,
							fascicolo.id,
							foundedAnagrafica.id,
						),
					];

					if (
						foundedAnagrafica.addrPaeseId !== null &&
						foundedAnagrafica.addrPaeseId > 0
					) {
						const paese = baseTableHelpers.FindInBaseTableFromId(
							basePaeseList,
							foundedAnagrafica.addrPaeseId,
						);
						if (addressHelpers.IsPaeseIta(paese)) {
							const provincia = baseTableHelpers.FindInBaseTableFromId(
								baseProvinciaList,
								foundedAnagrafica.addrProvinciaId,
							);
							if (provincia?.sigla) {
								loadBaseComune(provincia.sigla);
							}
						}
						setIsAddrPaeseIta(addressHelpers.IsPaeseIta(paese));
					}

					setShowOtherPatrimonio(foundedAnagrafica.tipoPatrimonioId === 5);
					setShowOtherReddito(foundedAnagrafica.tipoRedditoId === 6);
					// Recupero l'identificazione
					if (foundedAnagrafica) {
						if (foundedAnagrafica.modalitaIdentId) {
							const modalitaIdentKey = baseTableHelpers.FindInBaseTableFromId(
								baseTipoIdentList,
								foundedAnagrafica.modalitaIdentId,
							).key;
							setIsModIdentDiretta(
								modalitaIdentKey === "tipo_identificazione_1",
							);
							setShowDoctipoAp(modalitaIdentKey === "tipo_identificazione_5");
						}
						foundedAnagrafica.identifiedByUser =
							foundedAnagrafica.identifiedByUser === ""
								? loggedUserId
								: foundedAnagrafica.identifiedByUser;
						if (foundedAnagrafica.docTipoId > 0) {
							const docTipoKey = baseTableHelpers.FindInBaseTableFromId(
								baseTipoDocList,
								foundedAnagrafica.docTipoId,
							).key;
							setShowDocTipoDetails(docTipoKey !== "tipo_documento_3");
						}

						setIsCessato(loggedUserId !== foundedAnagrafica.identifiedByUser);
						setEnableFormControls(true);
					}

					setAnagPep((prevState) => ({
						...prevState,
						IsNotPep:
							!foundedAnagrafica.isPep &&
							!foundedAnagrafica.isNotPepMoreThanYear,
						IsPep: foundedAnagrafica.isPep,
						IsNotPepMoreThanYear: foundedAnagrafica.isNotPepMoreThanYear,
					}));
				} else if (anagraficheFromDB && anagraficheFromDB.length === 0) {
					foundedAnagrafica = await emptyAnagraficaModel(basePaeseList);
					foundedAnagrafica.cf = cf;
					foundedAnagrafica.nascitaData = nascitaData;
					foundedAnagrafica.nascitaLuogo = luogoNascita;
					foundedAnagrafica.genereId = genereId;
					foundedAnagrafica.isPg = isPG;
					foundedAnagrafica.identifiedByUser = loggedUserId;
					foundedAnagrafica.modalitaIdentId = 1; //Identificazione diretta
					foundedAnagrafica.anagraficaFascicoli = [
						getEmptyAnagraficaFascicoloModel(fascicolo.id, tipoAnag),
					];
				}

				if (anagraficheFromDB.length > 1) {
					setShowResultListModal(true);
					setResultList(anagraficheFromDB);
				} else {
					setCurrentAnagraficaOriginal(foundedAnagrafica);
					setCurrentAnagrafica(foundedAnagrafica);
					setShowAnagForm(true);
				}
				if (anagraficheFromDB.length === 1) {
					loadAndCompareCervedDatas(foundedAnagrafica, baseProvinciaList, tipoAnag);
				}

				setBackDropOpen(false);
			} else {
				enqueueSnackbar("Il campo di ricerca non può essere vuoto", {
					variant: "warning",
				});
			}
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "HandleSearchClick", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	const handleChangeDate = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentAnagrafica.nascitaData = newDate;
			setCurrentAnagrafica((prevState) => ({
				...prevState,
				nascitaData: newDate,
			}));
		}
		setIsVersionSavingDisabled(false);
	};
	const handleChangeVersionDate = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentAnagraficaNewVer.versionDate = newDate;
			setCurrentAnagraficaNewVer((prevState) => ({
				...prevState,
				versionDate: newDate,
			}));
		}
		setIsVersionSavingDisabled(false);
	};
	const handleChangeIdentDate = (date) => {
		const newDate = isDateValid(date) ? new Date(date) : null;
		currentAnagrafica.identifiedOn = newDate;

		if (currentAnagrafica.versionN === 1) {
			setCurrentAnagrafica((prevState) => ({
				...prevState,
				identifiedOn: newDate,
				versionDate: newDate,
			}));
		} else {
			setCurrentAnagrafica((prevState) => ({
				...prevState,
				identifiedOn: newDate,
			}));
		}

		// Aggiorno la sidebar solo se il record originale non aveva identifiedOn valorizzato
		if (!wasIdentifiedOnLoad) {
			// Aggiorno anche l'anagrafica corrispondente nella lista della sidebar in base al tipo
			const updateListItem = (list, setList) => {
				const updatedList = list.map((item) => {
					if (
						item.id === currentAnagrafica.id ||
						item.anagraficaId === currentAnagrafica.id
					) {
						return { ...item, identifiedOn: newDate };
					}
					return item;
				});
				setList(updatedList);
			};

			// Aggiorno la lista appropriata in base al tipo di anagrafica
			if (tipoAnag === "cliente") {
				updateListItem(
					currentAnagraficheClientiList,
					setCurrentAnagraficheClientiList,
				);
			} else if (tipoAnag === "collegate") {
				updateListItem(
					currentAnagraficheCollegateList,
					setCurrentAnagraficheCollegateList,
				);
			} else if (tipoAnag === "titolare") {
				updateListItem(
					currentAnagraficheTitEffList,
					setCurrentAnagraficheTitEffList,
				);
			}
		}
		setIsVersionSavingDisabled(false);
	};
	const handleChangeDocRilDate = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentAnagrafica.docRilData = newDate;
			setCurrentAnagrafica((prevState) => ({
				...prevState,
				docRilData: newDate,
			}));
		}
		setIsVersionSavingDisabled(false);
	};
	const handleChangeCessDate = (date) => {
		if (date !== null && isDateValid(date)) {
			let dt = date.setHours(12, 0, 0, 0);
			const newDate = new Date(dt).toISOString();
			currentAnagraficaFascicolo.dataFine = newDate;
			setCurrentAnagraficaFascicolo((prevState) => ({
				...prevState,
				dataFine: newDate,
			}));
		} else if (date === null) {
			currentAnagraficaFascicolo.dataFine = null;
			setCurrentAnagraficaFascicolo((prevState) => ({
				...prevState,
				dataFine: null,
			}));
		}
	};
	const handleBlurDate = () => {
		var selTipoIdent = baseTipoDocList.filter(
			(doc) => doc.id === currentAnagrafica.docTipoId,
		)[0];
		if (selTipoIdent.validityMonths) {
			var dateRil = new Date(currentAnagrafica.docRilData);
			if (selTipoIdent.nextBirthDay) {
				var expiryBase = new Date(dateRil);
				expiryBase.setMonth(
					expiryBase.getMonth() + selTipoIdent.validityMonths,
				);
				if (selTipoIdent.validityOffsetDays) {
					expiryBase.setDate(
						expiryBase.getDate() + selTipoIdent.validityOffsetDays,
					);
				}

				var birthDay = new Date(currentAnagrafica.nascitaData);
				var birthMonth = birthDay.getMonth();
				var birthDayOfMonth = birthDay.getDate();
				var rilMonth = dateRil.getMonth();
				var rilDay = dateRil.getDate();

				// Anno candidato: un anno prima di expiryBase
				var candidateYear = expiryBase.getFullYear() - 1;

				// Compleanno nell'anno candidato
				var candidate = new Date(candidateYear, birthMonth, birthDayOfMonth);

				// Data di rilascio proiettata nell'anno candidato (confronto solo mese/giorno)
				var rilInCandidateYear = new Date(candidateYear, rilMonth, rilDay);

				// Se il compleanno nell'anno candidato è già passato rispetto al giorno
				// di rilascio (stesso mese/giorno), significa che al momento del rilascio
				// il compleanno era già avvenuto quell'anno → il primo compleanno utile
				// è nell'anno successivo (expiryBase.year)
				if (candidate < rilInCandidateYear) {
					candidate.setFullYear(candidateYear + 1);
				}

				dateRil = candidate;
			} else {
				if (selTipoIdent.validityOffsetDays) {
					dateRil.setDate(dateRil.getDate() + selTipoIdent.validityOffsetDays);
				}
				dateRil.setMonth(dateRil.getMonth() + selTipoIdent.validityMonths);
				currentAnagrafica.docScad = dateRil;
			}

			setCurrentAnagrafica((prevState) => ({
				...prevState,
				docScad: dateRil,
			}));
		}
	};
	const handleChangeDocScad = (date) => {
		if (date !== null && isDateValid(date)) {
			const newDate = new Date(date);
			currentAnagrafica.docScad = newDate;
			setCurrentAnagrafica((prevState) => ({
				...prevState,
				docScad: newDate,
			}));
		}
		setIsVersionSavingDisabled(false);
	};
	const handleCopyAddress = () => {
		const updatedAnagrafica = {
			...currentAnagrafica,
			addr2PaeseId: currentAnagrafica.addrPaeseId,
			addr2Indirizzo: currentAnagrafica.addrIndirizzo,
			addr2N: currentAnagrafica.addrN,
			addr2Cap: currentAnagrafica.addrCap,
		};

		const country = basePaeseList.find(
			(item) => item.id === updatedAnagrafica.addr2PaeseId,
		);
		const isItalia = country?.identificativo === "ITA";

		if (isItalia) {
			updatedAnagrafica.addr2Provincia = "";
			updatedAnagrafica.addr2Comune = "";
			updatedAnagrafica.addr2ProvinciaId = currentAnagrafica.addrProvinciaId;
			updatedAnagrafica.addr2ComuneId = currentAnagrafica.addrComuneId;

			const provincia = baseProvinciaList.find(
				(item) => item.id === updatedAnagrafica.addr2ProvinciaId,
			);
			if (provincia?.sigla) {
				loadBaseComune2(provincia.sigla);
			}
		} else {
			updatedAnagrafica.addr2ProvinciaId = null;
			updatedAnagrafica.addr2ComuneId = null;
			updatedAnagrafica.addr2Provincia = currentAnagrafica.addrProvincia;
			updatedAnagrafica.addr2Comune = currentAnagrafica.addrComune;
		}

		setCurrentAnagrafica(updatedAnagrafica);
		setIsAddr2PaeseIta(isItalia);
		setIsVersionSavingDisabled(
			anagHelpers.CompareAnagrafiche(
				updatedAnagrafica,
				currentAnagraficaOriginal,
			),
		);
	};
	const handleAddNewAnagrafica = async (type) => {
		const newItem = await emptyAnagraficaModel(basePaeseList);
		const newItemAngFascicolo = emptyAnagraficaFascicoloModel();
		setTipoAnag(type);
		setIsPG(false);
		setLastSelectedAnag(null);
		setIsCessato(false);
		setClienteAnagType("PF");

		switch (type) {
			case "cliente": {
				newItemAngFascicolo.tipoAnagId = baseTipoAnagList.filter(
					(item) => item.key === TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
				)[0].id;
				break;
			}
			case "collegate": {
				newItemAngFascicolo.tipoAnagId = baseTipoAnagList.filter(
					(item) => item.key === TIPO_ANAGRAFICA_LEGALE_RAPP_KEY,
				)[0].id;
				break;
			}
			case "titolare": {
				newItemAngFascicolo.tipoAnagId = baseTipoAnagList.filter(
					(item) => item.key === TIPO_ANAGRAFICA_TITOLARE_EFFETTIVO_KEY,
				)[0].id;
				break;
			}
			default: {
				break;
			}
		}

		setCurrentAnagrafica(newItem);
		setCurrentAnagraficaOriginal(newItem);
		setCurrentAnagraficaFascicolo(newItemAngFascicolo);
		setWasIdentifiedOnLoad(false); // Nuova anagrafica non è identificata
		setCurrentAnagraficaNewVer(newItem);
		setShowDoctipoAp(false);
		setShowDocTipoDetails(false);
		setIsAddrPaeseIta(true);
		setAnagPep({ IsNotPep: true, IsPep: false, IsNotPepMoreThanYear: false });
		setShowTipoPartDesc(false);
		setIdToPerform(0);
		setEnableFormControls(true);
		setShowAnagForm(false);
		setIsNotLastVersion(false);

		//Invio una notifica tramite pub/sub interno
		postal.publish({
			channel: "fascicolo",
			topic: "anagrafica.detail",
			data: {
				anagrafica: newItem,
			},
		});
	};
	const handleCancelClick = async () => {
		setTipoAnag("cliente");
		setIsDataLoaded(false);
		setShowAnagForm(false);
		setLastSelectedAnag(null);
	};
	const handleSelectedAnagClick = async (anagrafica, tipoAnag) => {
		try {
			setIsVersionSavingDisabled(true);
			var historyItems = anagHelpers.SortVersionAnagrafiche(
				currentAnagraficheHistoryList.filter(
					(x) => x.id === anagrafica.id || x.anagraficaId === anagrafica.id,
				),
			);
			await selectAnagrafica(
				anagrafica,
				baseTipoIdentList,
				baseTipoDocList,
				basePaeseList,
				baseProvinciaList,
				baseTipoAnagList,
				baseTipoPartList,
				historyItems,
				tipoAnag,
			);
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleSelectedAnagClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	const handleSelectedAnagVerClick = async (anagrafica) => {
		try {
			setIsVersionSavingDisabled(true);
			await selectAnagrafica(
				anagrafica,
				baseTipoIdentList,
				baseTipoDocList,
				basePaeseList,
				baseProvinciaList,
				baseTipoAnagList,
				baseTipoPartList,
				currentAnagraficheHistoryFilteredList,
				tipoAnag,
			);
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleSelectedAnagClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	const handleDeleteAnagClick = (anagrafica, tipoAnagId) => {
		setIdToPerform(anagrafica.id);
		setTypeToPerform("A");
		setShowDeleteModal(true);
		if (anagrafica.anagraficaFascicoli.length > 0) {
			anagrafica.anagraficaFascicoli = anagrafica.anagraficaFascicoli.filter(
				(anagFasc) => anagFasc.tipoAnagId === tipoAnagId,
			);
		}
		setAnagFascToPerform(
			anagrafica.anagraficaFascicoli.length > 0
				? anagrafica.anagraficaFascicoli[0]
				: null,
		);
	};
	const handleDeleteAnagVerClick = (anagrafica) => {
		setIdToPerform(anagrafica.id);
		setTypeToPerform("AV");
		setShowDeleteModal(true);
	};
	const handleSaveAnagClick = async () => {
		var errors = await anagHelpers.CheckAnagraficaBeforeSave(
			currentAnagrafica,
			currentAnagraficaFascicolo,
			baseTipoDocList,
			baseTipoAnagList,
			clienteAnagType,
			baseTipoIdentList,
		);
		if (errors.length > 0) {
			for (let i = 0; i < errors.length; i++) {
				enqueueSnackbar(errors[i], { variant: "error" });
			}
			return;
		}
		// Validazione: CF o P.IVA modificati richiedono nuova versione
		if (currentAnagrafica.id !== 0 && currentAnagrafica.modifiedOn) {
			const cfChanged = currentAnagrafica.cf !== currentAnagraficaOriginal.cf;
			const pivaChanged =
				currentAnagrafica.pIva !== currentAnagraficaOriginal.pIva;

			if (cfChanged || pivaChanged) {
				let message = "Non è possibile salvare le modifiche. ";
				if (cfChanged && pivaChanged) {
					message +=
						"Il Codice Fiscale e la Partita IVA sono stati modificati.";
				} else if (cfChanged) {
					message += "Il Codice Fiscale è stato modificato.";
				} else {
					message += "La Partita IVA è stata modificata.";
				}
				message += " È necessario creare una nuova versione dell'anagrafica.";

				enqueueSnackbar(message, { variant: "error", autoHideDuration: 7500 });
				return;
			}
		}
		// Validazione: la data di identificazione non può essere inferiore a quella della versione 1
		if (
			currentAnagrafica.versionN > 1 &&
			currentAnagraficheHistoryFilteredList.length > 0 &&
			tipoAnag === "cliente"
		) {
			const version1 = currentAnagraficheHistoryFilteredList.find(
				(v) => v.versionN === 1,
			);
			if (
				version1 &&
				isDateValid(version1.identifiedOn) &&
				isDateValid(currentAnagrafica.identifiedOn)
			) {
				const date1 = new Date(version1.identifiedOn);
				const currentDate = new Date(currentAnagrafica.identifiedOn);
				if (currentDate < date1) {
					enqueueSnackbar(
						"La data di identificazione non può essere inferiore alla data di identificazione della prima versione (" +
							date1.toLocaleDateString() +
							")",
						{ variant: "error" },
					);
					return;
				}
			}
		}

		if (currentAnagrafica.identifiedByUser !== loggedUserId) {
			setShowIdentModal(true);
			setSaveType("anagrafica");
		} else {
			if (currentAnagrafica.id === 0) {
				await saveAnagrafica();
			} else {
				await updateAnagrafica();
			}
		}
	};
	const handleSaveAnagNewVersionClick = async () => {
		try {
			setBackDropOpen(true);
			var errors = await anagHelpers.CheckAnagraficaBeforeSave(
				currentAnagrafica,
				currentAnagraficaFascicolo,
				baseTipoDocList,
				baseTipoAnagList,
				clienteAnagType,
				baseTipoIdentList,
			);
			if (errors.length <= 0) {
				// Validazione: la data di identificazione non può essere inferiore a quella della versione 1
				if (
					currentAnagrafica.versionN > 1 &&
					currentAnagraficheHistoryFilteredList.length > 0
				) {
					const version1 = currentAnagraficheHistoryFilteredList.find(
						(v) => v.versionN === 1,
					);
					if (
						version1 &&
						isDateValid(version1.identifiedOn) &&
						isDateValid(currentAnagrafica.identifiedOn)
					) {
						const date1 = new Date(version1.identifiedOn);
						const currentDate = new Date(currentAnagrafica.identifiedOn);
						if (currentDate < date1) {
							enqueueSnackbar(
								"La data di identificazione non può essere inferiore alla data di identificazione della prima versione (" +
									date1.toLocaleDateString() +
									")",
								{ variant: "error" },
							);
							setBackDropOpen(false);
							return;
						}
					}
				}

				const newAnagVer = Object.assign({}, currentAnagrafica);
				// Imposta la data di storicizzazione proposta uguale alla data della versione corrente
				newAnagVer.versionDate =
					currentAnagrafica.versionDate || currentAnagrafica.identifiedOn;
				newAnagVer.versionN = newAnagVer.versionN + 1;
				setCurrentAnagraficaNewVer(newAnagVer);
				setIdToPerform(newAnagVer.id);
				setShowHistoryModal(true);
			} else {
				for (let i = 0; i < errors.length; i++) {
					enqueueSnackbar(errors[i], { variant: "error" });
				}
			}
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleSaveAnagNewVersionClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	//#endregion

	//#region CRUD dell'anagrafica
	const emptyAnagraficaModel = async (paesi) => {
		const paeseId = paesi.filter((x) => x.identificativo === "ITA")[0].id;
		setIsAddrPaeseIta(true);
		const newItem = getEmptyAnagraficaModel(paeseId, loggedUserId);
		return newItem;
	};
	const emptyAnagraficaFascicoloModel = () => {
		const newItem = getEmptyAnagraficaFascicoloModel(fascicolo.id);
		return newItem;
	};
	const selectAnagrafica = async (
		anagrafica,
		arrTipoIdentItems,
		arrBaseTipoDoc,
		arrBasePaese,
		arrBaseProvincia,
		arrTipoAnag,
		arrBaseTipoPart,
		anagHistory,
		tipoAnagrafica,
	) => {
		try {
			// Clona subito l'anagrafica per evitare di modificare l'oggetto nelle liste della sidebar
			anagrafica = Object.assign(new anagraficaModel(), anagrafica);
			// Salva lo stato originale prima di qualsiasi modifica
			const wasOriginallyIdentified = !!anagrafica.identifiedOn;
			setShowDoctipoAp(false);
			setIsModIdentDiretta(false);
			setShowDocTipoDetails(false);
			setShowAnagForm(true);
			setShowOtherPatrimonio(anagrafica.tipoPatrimonioId === 5);
			setShowOtherReddito(anagrafica.tipoRedditoId === 6);

			//Recupero l'identificazione effettuata dall'utente corrente
			if (anagrafica) {
				anagrafica.modalitaIdentId = anagrafica.modalitaIdentId || 1;
				const wasIdentifiedOnNull = !anagrafica.identifiedOn;
				// Se la data di identificazione era null, imposta anche la data di versione
				if (wasIdentifiedOnNull) {
					anagrafica.versionDate = anagrafica.identifiedOn;
				}
				const modalitaIdentKey =
					baseTableHelpers.FindKeyInBaseTableFromIdOrDefault(
						arrTipoIdentItems,
						anagrafica.modalitaIdentId,
					);
				setIsModIdentDiretta(modalitaIdentKey === "tipo_identificazione_1");
				setShowDoctipoAp(modalitaIdentKey === "tipo_identificazione_5");
				anagrafica.docTipoId = anagrafica.docTipoId || 1;
				const docTipoKey = baseTableHelpers.FindKeyInBaseTableFromIdOrDefault(
					arrBaseTipoDoc,
					anagrafica.docTipoId,
				);
				setShowDocTipoDetails(docTipoKey !== "tipo_documento_3");
			}

			if (
				anagrafica.addrPaeseId &&
				anagrafica.addrPaeseId !== null &&
				anagrafica.addrPaeseId !== 0
			) {
				const paeseFiltered = arrBasePaese.filter(
					(basePaese) => basePaese.id === anagrafica.addrPaeseId,
				);
				if (paeseFiltered.length > 0) {
					const paese = paeseFiltered[0];
					if (paese.identificativo === "ITA") {
						if (
							anagrafica.addrProvinciaId !== null &&
							anagrafica.addrProvinciaId !== 0
						) {
							const provinciaFiltered = arrBaseProvincia.filter(
								(baseProvincia) =>
									baseProvincia.id === anagrafica.addrProvinciaId,
							);
							if (provinciaFiltered.length > 0) {
								await loadBaseComune(provinciaFiltered[0].sigla);
							}
						}
					}
					setIsAddrPaeseIta(paese.identificativo === "ITA");
				}
			} else {
				const itaPaeseFiltered = arrBasePaese.filter(
					(basePaese) => basePaese.identificativo === "ITA",
				);
				if (itaPaeseFiltered.length > 0) {
					anagrafica.addrPaeseId = itaPaeseFiltered[0].id;
				}
				setIsAddrPaeseIta(true);
			}

			setCurrentAnagrafica(anagrafica);
			setCurrentAnagraficaOriginal(
				Object.assign(new anagraficaModel(), anagrafica),
			);
			setWasIdentifiedOnLoad(wasOriginallyIdentified);

			let tipoAnagKey = arrTipoAnag.filter(
				(x) => x.id === anagHelpers.GetIdFromTipoAnagrafica(tipoAnagrafica),
			)[0].key;
			let anagFascicolo = null;
			if (anagrafica.anagraficaFascicoli.length > 1) {
				if (anagHelpers.IsAnagraficaCliente(tipoAnagKey)) {
					anagFascicolo = anagHelpers.GetFascicoliCliente(anagrafica);
				} else if (anagHelpers.IsAnagraficaCollegata(tipoAnagKey)) {
					anagFascicolo = anagHelpers.GetFascicoliCollegate(anagrafica);
				} else if (anagHelpers.IsAnagraficaTitolareEffettivo(tipoAnagKey)) {
					anagFascicolo = anagHelpers.GetFascicoliTitolareEffettivo(anagrafica);
				}
			} else {
				anagFascicolo =
					anagrafica.anagraficaFascicoli[0] || currentAnagraficaFascicolo;
			}
			setTipoAnag(tipoAnagrafica);
			setClienteAnagType(anagHelpers.GetTipoCliente(anagrafica));
			setCurrentAnagraficaFascicolo(
				new anagraficaFascicoloModel(
					anagFascicolo.id,
					anagrafica.id,
					fascicolo.id,
					anagFascicolo.tipoAnagId,
					anagFascicolo.tipoPartId,
					anagFascicolo.tipoPartDesc,
					anagFascicolo.dataFine !== undefined ? anagFascicolo.dataFine : null,
					anagFascicolo.tipoRappId,
					anagFascicolo.tipoRappDesc,
				),
			);
			if (
				anagrafica.addr2PaeseId &&
				anagrafica.addr2PaeseId !== null &&
				anagrafica.addr2PaeseId !== 0
			) {
				const paese2Filtered = arrBasePaese.filter(
					(basePaese) => basePaese.id === anagrafica.addr2PaeseId,
				);
				if (paese2Filtered.length > 0) {
					const paese = paese2Filtered[0];
					if (paese.identificativo === "ITA") {
						const provincia2Filtered = arrBaseProvincia.filter(
							(baseProvincia) =>
								baseProvincia.id === anagrafica.addr2ProvinciaId,
						);
						if (provincia2Filtered.length > 0) {
							await loadBaseComune2(provincia2Filtered[0].sigla);
						}
					}
					setIsAddr2PaeseIta(paese.identificativo === "ITA");
				}
			}
			setIsPG(anagrafica.isPg);
			setAnagPep({
				IsNotPep: !anagrafica.isPep && !anagrafica.isNotPepMoreThanYear,
				IsPep: anagrafica.isPep && !anagrafica.isNotPepMoreThanYear,
				IsNotPepMoreThanYear: anagrafica.isNotPepMoreThanYear,
			});
			setIdToPerform(0);
			setShowTipoPartDesc(
				anagFascicolo.tipoPartId ===
					arrBaseTipoPart.filter(
						(baseTipoPart) =>
							baseTipoPart.key === TIPO_PARTECIPAZIONE_ALTRO_KEY,
					)[0].id,
			);

			//Setto lo storico
			let historyLastItem = anagHistory[0];
			if (anagHistory.length > 0) {
				if (historyLastItem !== null) {
					setEnableFormControls(
						anagrafica.versionN === historyLastItem.versionN,
					);
				} else {
					setEnableFormControls(true);
				}
				setCurrentAnagraficheHistoryFilteredList(anagHistory);

				if (anagHistory.length > 0) {
					const newItemModel = Object.assign({}, anagHistory[0]);
					newItemModel.anagraficaFascicoli = anagrafica.anagraficaFascicoli;
					setLastSelectedAnag(newItemModel);
				} else {
					setLastSelectedAnag(anagrafica);
				}
			} else {
				setLastSelectedAnag(anagrafica);
			}

			//Invio una notifica tramite pub/sub interno
			postal.publish({
				channel: "fascicolo",
				topic: "anagrafica.detail",
				data: {
					anagrafica: anagrafica,
				},
			});

			//Chiudo il menù a tendina delle versioni
			handleClose();
			let date = new Date();
			date.setHours(0, 0, 0, 0);
			setIsNotLastVersion(historyLastItem.versionN > anagrafica.versionN);
			setIsCessato(
				anagFascicolo.dataFine &&
					date >= new Date(anagFascicolo.dataFine).setHours(0, 0, 0, 0),
			);
			loadAndCompareCervedDatas(anagrafica, arrBaseProvincia, tipoAnagrafica);
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "SelectAnagrafica", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	const saveAnagrafica = async () => {
		try {
			setBackDropOpen(true);
			//Se sto creando l'anagrafica
			let dataIdent = dateHelpers.FormatDateToUTC(
				new Date(currentAnagrafica.identifiedOn),
			);
			let dtNascita = dateHelpers.FormatToUTCWithoutTimezone(
				currentAnagrafica.nascitaData,
				false,
			);
			currentAnagrafica.nascitaData = dtNascita;
			currentAnagrafica.versionDate = dataIdent;
			currentAnagrafica.tenantId = tenantId;
			if (isModIdentArt22) {
				currentAnagrafica.docTipoId = null;
				currentAnagrafica.docN = "";
				currentAnagrafica.docRilDa = "";
				currentAnagrafica.docRilData = null;
				currentAnagrafica.docScad = null;
			} else {
				currentAnagrafica.docRilData =
					currentAnagrafica.isPg && currentAnagrafica.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagrafica.docRilData,
								false,
							);
				currentAnagrafica.docScad =
					currentAnagrafica.isPg && currentAnagrafica.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagrafica.docScad,
								false,
							);
			}
			currentAnagrafica.identifiedOn = dateHelpers.FormatToUTCWithoutTimezone(
				currentAnagrafica.identifiedOn,
				false,
			);
			currentAnagrafica.countryCode = "IT";
			let anagraficaPostResponse = null;
			let anagraficaResultModel = null;
			let newBancheDatiItems = [];
			let tipoAnagKey = null;

			try {
				anagraficaPostResponse =
					await anagraficaApi.PostAnagrafica(currentAnagrafica);
			} catch (error) {
				logApi.AddLog(
					error.message,
					error.stack,
					"saveAnagrafica/PostAnagrafica",
					"AnagraficaForm",
				);
				return enqueueSnackbar(
					"Errore durante il salvataggio dell'anagrafica: " + error.message,
					{ variant: "error" },
				);
			}

			if (anagraficaPostResponse) {
				anagraficaResultModel = createAnagraficaModel(anagraficaPostResponse);
				setShowOtherPatrimonio(anagraficaResultModel.tipoPatrimonioId === 5);
				setShowOtherReddito(anagraficaResultModel.tipoRedditoId === 6);
				//Aggiorno il nome del fascicolo solo se l'anagrafica è di tipo Cliente
				tipoAnagKey = baseTipoAnagList.filter(
					(bta) => bta.id === currentAnagraficaFascicolo.tipoAnagId,
				)[0].key;
				if (anagHelpers.IsAnagraficaCliente(tipoAnagKey)) {
					fascicolo.tenantId = tenantId;
					fascicolo.aziendaId = aziendaId;
					try {
						await fascicoloApi.UpdateFascicolo(fascicolo, fascicolo.isPrivate);
					} catch (error) {
						enqueueSnackbar(error, { variant: "error" });
					}
				}
				let bancheDatiResult = await loadBancheDati(anagraficaResultModel);
				newBancheDatiItems.push(...bancheDatiResult);
			}
			setAnagraficheBancaDatiList(newBancheDatiItems);

			if (currentAnagraficaFascicolo.id === 0) {
				currentAnagraficaFascicolo.anagraficaId = anagraficaResultModel.id;
				let postanagFascicoloResponse =
					await anagraficaFascicoloApi.PostAnagraficaFascicolo(
						currentAnagraficaFascicolo,
						fascicolo.id,
					);

				if (postanagFascicoloResponse) {
					const anagraficaFascicoloResult = createAnagraficaFascicoloModel(
						postanagFascicoloResponse,
						fascicolo.id,
						postanagFascicoloResponse.AnagraficaId,
					);
					anagraficaResultModel.anagraficaFascicoli = [
						anagraficaFascicoloResult,
					];
					setCurrentAnagraficaFascicolo(anagraficaFascicoloResult);
				}
			}

			//Aggiungo la Audit per tracciare l'azione
			const newValues = anagraficaResultModel;
			apiAddAudit.fetch({
				tenantId: tenantId,
				aziendaId: aziendaId,
				area: bsaAnagrafica,
				operation: "Create",
				entityType: "anagrafica",
				entityId: anagraficaResultModel.id.toString(),
				oldValues: "",
				newValues: JSON.stringify(newValues),
			});
			//Aggiornamento item del workspace
			try {
				await workspaceApi.UpdateItem(anagraficaResultModel.id);
			} catch (e) {
				logApi.AddLog(
					`${e} while saving on workspace anagrafica with id ${anagraficaResultModel.id}`,
				);
			}
			setLastSelectedAnag(anagraficaResultModel);
			enqueueSnackbar(t("message.success"), { variant: "success" });
		} catch (e) {
			logApi.AddLog(e.message, e.stack, "SaveAnagrafica", "AnagraficaForm");
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
			await reloadStatusFascicolo();
			setIsDataLoaded(false);
		}
	};
	const updateAnagrafica = async () => {
		try {
			setBackDropOpen(true);
			if (isPIvaChanged) {
				await apiUpdatePIvaAnagrafica.fetch({
					id: currentAnagrafica.id,
					PIva: currentAnagrafica.pIva,
					errWs: true,
					errWsNote: `Variazione P.Iva da ${currentAnagraficaOriginal.pIva} a ${currentAnagrafica.pIva}`,
				});
				setIsPIvaChanged(false);
			}

			let dtNascita = currentAnagrafica.nascitaData
				? dateHelpers.FormatToUTCWithoutTimezone(
						currentAnagrafica.nascitaData,
						false,
					)
				: null;
			currentAnagrafica.nascitaData = dtNascita;
			if (isModIdentArt22) {
				currentAnagrafica.docTipoId = null;
				currentAnagrafica.docN = "";
				currentAnagrafica.docRilDa = "";
				currentAnagrafica.docRilData = null;
				currentAnagrafica.docScad = null;
			} else {
				currentAnagrafica.docRilData =
					currentAnagrafica.isPg && currentAnagrafica.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagrafica.docRilData,
								false,
							);
				currentAnagrafica.docScad =
					currentAnagrafica.isPg && currentAnagrafica.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagrafica.docScad,
								false,
							);
			}
			currentAnagrafica.identifiedOn = dateHelpers.FormatToUTCWithoutTimezone(
				currentAnagrafica.identifiedOn,
				false,
			);
			let updateAnagraficaResponse = null;
			let anagraficaResultModel = null;
			try {
				updateAnagraficaResponse =
					await anagraficaApi.UpdateAnagrafica(currentAnagrafica);
			} catch (error) {
				logApi.AddLog(
					error.message,
					error.stack,
					"updateAnagrafica/UpdateAnagrafica",
					"AnagraficaForm",
				);
				return enqueueSnackbar(
					"Errore durante l'aggiornamento dell'anagrafica: " + error.message,
					{ variant: "error" },
				);
			}

			if (updateAnagraficaResponse) {
				anagraficaResultModel = createAnagraficaModel(updateAnagraficaResponse);
				setShowOtherPatrimonio(anagraficaResultModel.tipoPatrimonioId === 5);
				setShowOtherReddito(anagraficaResultModel.tipoRedditoId === 6);
				//Aggiorno la data del fascicolo solo se l'anagrafica è di tipo Cliente
				const tipoAnagKey = baseTipoAnagList.filter(
					(bta) => bta.id === currentAnagraficaFascicolo.tipoAnagId,
				)[0].key;
				if (anagHelpers.IsAnagraficaCliente(tipoAnagKey)) {
					let dataFascicolo = fascicolo.date;
					if (anagraficaResultModel.versionN === 1) {
						const dataIdent = new Date(currentAnagrafica.identifiedOn);
						const dataIdentD = new Date(
							Date.UTC(
								dataIdent.getFullYear(),
								dataIdent.getMonth(),
								dataIdent.getDate(),
								12,
								0,
								0,
							),
						);
						dataFascicolo = dataIdentD;
					}
					fascicolo.name = anagraficaResultModel.denominazione
						? anagraficaResultModel.denominazione
						: anagraficaResultModel.nome + " " + anagraficaResultModel.cognome;
					fascicolo.date = dateHelpers.FormatToUTCWithoutTimezone(
						dataFascicolo,
						false,
					);
					fascicolo.tenantId = tenantId;
					fascicolo.aziendaId = aziendaId;
					try {
						await fascicoloApi.UpdateFascicolo(fascicolo, fascicolo.isPrivate);
					} catch (error) {
						enqueueSnackbar(error, { variant: "error" });
					}
				}

				let anagraficaFascicoloResult = null;
				if (currentAnagraficaFascicolo.id === 0) {
					currentAnagraficaFascicolo.anagraficaId = anagraficaResultModel.id;
					currentAnagraficaFascicolo.dataFine =
						currentAnagraficaFascicolo.dataFine
							? dateHelpers.FormatToUTCWithoutTimezone(
									currentAnagraficaFascicolo.dataFine,
									false,
								)
							: null;
					let postanagFascicoloResponse =
						await anagraficaFascicoloApi.PostAnagraficaFascicolo(
							currentAnagraficaFascicolo,
							fascicolo.id,
						);
					if (postanagFascicoloResponse) {
						anagraficaFascicoloResult = createAnagraficaFascicoloModel(
							postanagFascicoloResponse,
							fascicolo.id,
							postanagFascicoloResponse.AnagraficaId,
						);
					}
				} else {
					currentAnagraficaFascicolo.dataFine =
						currentAnagraficaFascicolo.dataFine
							? dateHelpers.FormatToUTCWithoutTimezone(
									currentAnagraficaFascicolo.dataFine,
									false,
								)
							: null;
					let updateAnagFascicoloResponse =
						await anagraficaFascicoloApi.UpdateAnagraficaFascicolo(
							currentAnagraficaFascicolo,
							fascicolo.id,
						);
					if (updateAnagFascicoloResponse) {
						anagraficaFascicoloResult = createAnagraficaFascicoloModel(
							updateAnagFascicoloResponse,
							fascicolo.id,
							updateAnagFascicoloResponse.anagraficaId,
						);
					}
				}
				anagraficaResultModel.anagraficaFascicoli = [anagraficaFascicoloResult];
				setCurrentAnagraficaFascicolo(anagraficaFascicoloResult);
				//Aggiungo la Audit per tracciare l'azione
				const newValues = anagraficaResultModel;
				apiAddAudit.fetch({
					tenantId: tenantId,
					aziendaId: aziendaId,
					area: bsaAnagrafica,
					operation: "Update",
					entityType: "anagrafica",
					entityId: anagraficaResultModel.id.toString(),
					oldValues: JSON.stringify(currentAnagraficaOriginal),
					newValues: JSON.stringify(newValues),
				});

				try {
					await workspaceApi.UpdateItem(anagraficaResultModel.id);
				} catch (error) {
					logApi.AddLog(
						`${error} while saving on workspace anagrafica with id ${anagraficaResultModel.id}`,
					);
				}

				setCurrentAnagrafica(anagraficaResultModel);
			}
			setLastSelectedAnag(anagraficaResultModel);
			enqueueSnackbar(t("message.success"), { variant: "success" });
		} catch (error) {
			logApi.AddLog(
				error.message,
				error.stack,
				"UpdateAnagrafica",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
			await reloadStatusFascicolo();
			setIsDataLoaded(false);
		}
	};
	const saveAnagraficaVersione = async () => {
		try {
			setBackDropOpen(true);
			let dtNascita = currentAnagraficaNewVer.nascitaData
				? dateHelpers.FormatToUTCWithoutTimezone(
						currentAnagraficaNewVer.nascitaData,
						false,
					)
				: null;
			currentAnagraficaNewVer.nascitaData = dtNascita;
			currentAnagraficaNewVer.versionDate = dateHelpers.FormatDateToUTC(
				new Date(currentAnagraficaNewVer.versionDate),
			);
			if (!isModIdentArt22) {
				currentAnagraficaNewVer.docRilData =
					currentAnagraficaNewVer.isPg &&
					currentAnagraficaNewVer.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagraficaNewVer.docRilData,
								false,
							);
				currentAnagraficaNewVer.docScad =
					currentAnagraficaNewVer.isPg &&
					currentAnagraficaNewVer.docTipoId === 3
						? null
						: dateHelpers.FormatToUTCWithoutTimezone(
								currentAnagraficaNewVer.docScad,
								false,
							);
			}
			currentAnagraficaNewVer.identifiedOn =
				dateHelpers.FormatToUTCWithoutTimezone(
					currentAnagraficaNewVer.identifiedOn,
					false,
				);

			let response = await anagraficaApi.UpgradeAnagraficaVersion(
				currentAnagraficaNewVer,
			);
			if (isPIvaChanged) {
				const allAnagrafiche = await apiAnagraficaByFascicolo.fetch({
					fascicoloId: currentAnagraficaFascicolo.fascicoloId,
				});
				let anagClienti = allAnagrafiche.data.data.anagrafica.filter((item) =>
					item.anagrafica_fascicolos.some(
						(fascicolo) => fascicolo.TipoAnagId === 2,
					),
				);
				anagClienti.forEach(async (cliente) => {
					await apiUpdatePIvaAnagrafica.fetch({
						id: cliente.Id,
						PIva: currentAnagraficaNewVer.pIva,
						errWs: true,
						errWsNote: `Variazione P.Iva da ${currentAnagraficaOriginal.pIva} a ${currentAnagraficaNewVer.pIva}`,
					});
				});
				setIsPIvaChanged(false);
			}
			if (response && tipoAnag === "cliente") {
				fascicolo.name = currentAnagraficaNewVer.denominazione;
				fascicolo.tenantId = tenantId;
				fascicolo.aziendaId = aziendaId;
				await fascicoloApi.UpdateFascicolo(fascicolo, fascicolo.isPrivate);				
			}
			if (response) {
				setCurrentAnagraficaNewVer(await emptyAnagraficaModel(basePaeseList));
				setLastSelectedAnag(null);
				setIsDataLoaded(false);
				setTipoAnag("cliente");
				enqueueSnackbar(t("message.success"), { variant: "success" });
			}
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"SaveAnagraficaVersione",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setIdToPerform(0);
			setShowHistoryModal(false);
			setBackDropOpen(false);
			reloadStatusFascicolo();
		}
	};
	//#endregion

	//#region Utils
	const checkCfIsCorrect = async () => {
		var response = await apiBaseAll.fetch({ tenantId: tenantId });
		var allComuni = response.data.data.base_comune;
		var allPaesi = response.data.data.base_paese;
		var generatdCf = isFiscalCodeValid(
			currentAnagrafica.nome,
			currentAnagrafica.cognome,
			currentAnagrafica.genereId,
			currentAnagrafica.nascitaData,
			currentAnagrafica.nascitaLuogo,
			allComuni,
			allPaesi,
		);

		var isCorrect = generatdCf === currentAnagrafica.cf;
		return isCorrect;
	};
	const getTipoAnagIdForDelete = (anagrafica, tipoKeys) => {
		const allowedIds = baseTipoAnagList
			.filter((bta) => tipoKeys.includes(bta.key))
			.map((bta) => bta.id);

		const match = anagrafica?.anagraficaFascicoli?.find((af) =>
			allowedIds.includes(af.tipoAnagId),
		);

		return (
			match?.tipoAnagId ?? anagrafica?.anagraficaFascicoli?.[0]?.tipoAnagId ?? 0
		);
	};
	const checkAnagAlreadyInFascicolo = (Cf) => {
		if (
			tipoAnag === "collegate" &&
			currentAnagraficheCollegateList.length > 0
		) {
			return currentAnagraficheCollegateList.some((a) => a.cf === Cf);
		}
	};
	//#endregion

	//#region Modals handlers
	//Modal User Identificazione
	const [saveType, setSaveType] = useState("anagrafica");
	const handleModalIdentClose = async () => {
		setShowIdentModal(false);
		setShowHistoryModal(false);
	};
	const handleModalIdentOkClick = async () => {
		if (saveType === "anagrafica") {
			setShowIdentModal(false);
			if (currentAnagrafica.id === 0) {
				await saveAnagrafica();
			} else {
				await updateAnagrafica();
			}
		} else if (saveType === "version") {
			setShowIdentModal(false);
			setShowHistoryModal(false);
			await saveAnagraficaVersione();
		}
	};

	//Modal History
	const handleModalHistoryClose = async () => {
		setCurrentAnagraficaNewVer(await emptyAnagraficaModel(basePaeseList));
		setIdToPerform(0);
		setShowHistoryModal(false);
	};
	const handleModalHistoryOkClick = async () => {
		if (currentAnagrafica.identifiedByUser !== loggedUserId) {
			setShowIdentModal(true);
			setSaveType("version");
		} else {
			await saveAnagraficaVersione();
		}
	};

	//Modal Delete
	const handleModalDeleteClose = () => {
		setIdToPerform(0);
		setTypeToPerform("A");
		setShowDeleteModal(false);
		setAnagFascToPerform(null);
	};
	const handleModalDeleteOkClick = async () => {
		try {
			if (idToPerform > 0) {
				setBackDropOpen(true);

				if (typeToPerform === "A") {
					await anagraficaFascicoloApi.DeleteAnagraficaFascicolo(
						fascicolo.id,
						anagFascToPerform.id,
					);
					setLastSelectedAnag(null);
					setShowAnagForm(false);
					enqueueSnackbar(t("message.success"), { variant: "success" });
				} else if (typeToPerform === "AV") {
					let response = await anagraficaApi.DowngradeAnagraficaVersion(idToPerform);
					if (response && tipoAnag === "cliente") {
						fascicolo.name = response.denominazione;
						fascicolo.tenantId = tenantId
						fascicolo.aziendaId = aziendaId;
						await fascicoloApi.UpdateFascicolo(fascicolo, fascicolo.isPrivate);
					}
					if (response) {
						setCurrentAnagraficaNewVer(
							await emptyAnagraficaModel(basePaeseList),
						);
						setLastSelectedAnag(null);
					}
					enqueueSnackbar(t("message.success"), { variant: "success" });
				} else if (typeToPerform === "F") {
					//Cancello il fascicolo che non contiene nessuna anagrafica
					fascicoloApi.DeleteFascicolo(idToPerform);
					navigate("/fascicolo");
				}
			}
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleModalDeleteOkClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setIdToPerform(0);
			setTypeToPerform("A");
			setShowDeleteModal(false);
			setBackDropOpen(false);
			setTipoAnag("cliente");
			await reloadStatusFascicolo();
			setIsDataLoaded(false);
		}
	};

	//Modal confirm save standard
	const checkSaveConfirmNeed = async () => {
		try {
			if (!currentAnagrafica.isPg) {
				if (await checkCfIsCorrect()) {
					await handleSaveAnagClick();
				} else {
					setIdToPerform(currentAnagrafica.id > 0 ? currentAnagrafica.id : 1);
					setTypeToPerform("A");
					setShowSaveConfirmModal(true);
					setAnagFascToPerform(
						currentAnagrafica.anagraficaFascicoli.length > 0
							? currentAnagrafica.anagraficaFascicoli[0]
							: null,
					);
				}
			} else {
				await handleSaveAnagClick();
			}
		} catch (e) {
			enqueueSnackbar(e.message, { variant: "error" });
		}
	};
	const handleModalConfirmSaveClose = () => {
		setIdToPerform(0);
		setTypeToPerform("A");
		setShowSaveConfirmModal(false);
		setAnagFascToPerform(null);
	};
	const handleModalConfirmSaveOkClick = async () => {
		try {
			if (idToPerform > 0) {
				setBackDropOpen(true);
				setShowSaveConfirmModal(false);
				handleSaveAnagClick();
			}
		} catch (e) {
			console.log(e);
		} finally {
			setIdToPerform(0);
			setTypeToPerform("A");
			setBackDropOpen(false);
			reloadStatusFascicolo();
		}
	};

	//Modal confirm save version
	const checkSaveConfirmNeedVer = async () => {
		try {
			if (!currentAnagrafica.isPg) {
				if (await checkCfIsCorrect()) {
					await handleSaveAnagNewVersionClick();
				} else {
					setIdToPerform(currentAnagrafica.id);
					setTypeToPerform("A");
					setShowSaveConfirmModalVer(true);
					setAnagFascToPerform(
						currentAnagrafica.anagraficaFascicoli[0] ?? null,
					);
				}
			} else {
				await handleSaveAnagNewVersionClick();
			}
		} catch (e) {
			enqueueSnackbar(e.message, { variant: "error" });
		}
	};
	const handleModalConfirmSaveVerClose = () => {
		setIdToPerform(0);
		setTypeToPerform("A");
		setShowSaveConfirmModalVer(false);
		setShowHistoryModal(false);
		setAnagFascToPerform(null);
	};
	const handleModalConfirmSaveOkClickVer = async () => {
		try {
			if (idToPerform > 0) {
				setBackDropOpen(true);
				setShowSaveConfirmModalVer(false);
				handleSaveAnagNewVersionClick();
			}
		} catch (e) {
			console.log(e);
		} finally {
			setIdToPerform(0);
			setTypeToPerform("A");
			setBackDropOpen(false);
			reloadStatusFascicolo();
		}
	};
	const handleModalResultClickClose = () => {
		setShowResultListModal(false);
		setResultList([]);
	};
	const handleModalResultOkClick = async (evt, selectedAnagraficaId) => {
		try {
			const anagraficaFromApi =
				await anagraficaApi.GetAnagraficaById(selectedAnagraficaId);

			if (anagraficaFromApi) {
				let anagrafica = createAnagraficaModel(anagraficaFromApi);
				if (checkAnagAlreadyInFascicolo(anagrafica.cf)) {
					setBackDropOpen(false);
					enqueueSnackbar(
						"L'anagrafica selezionata è già presente nel fascicolo",
						{ variant: "warning", autoHideDuration: 7000 },
					);
					return;
				}
				if (anagrafica.addrPaeseId && anagrafica.addrPaeseId > 0) {
					const paese = baseTableHelpers.FindInBaseTableFromId(
						basePaeseList,
						anagrafica.addrPaeseId,
					);
					if (addressHelpers.IsPaeseIta(paese)) {
						const provincia = baseTableHelpers.FindInBaseTableFromId(
							baseProvinciaList,
							anagrafica.addrProvinciaId,
						);
						loadBaseComune(provincia.sigla);
					}
					setIsAddrPaeseIta(addressHelpers.IsPaeseIta(paese));
				}

				setShowOtherPatrimonio(anagrafica.tipoPatrimonioId === 5);
				setShowOtherReddito(anagrafica.tipoRedditoId === 6);
				// Recupero l'identificazione
				if (anagrafica) {
					const modalitaIdentKey = baseTableHelpers.FindInBaseTableFromId(
						baseTipoIdentList,
						anagrafica.modalitaIdentId,
					).key;
					setIsModIdentDiretta(modalitaIdentKey === "tipo_identificazione_1");
					setShowDoctipoAp(modalitaIdentKey === "tipo_identificazione_5");
					anagrafica.identifiedByUser =
						anagrafica.identifiedByUser === ""
							? loggedUserId
							: anagrafica.identifiedByUser;

					if (anagrafica.docTipoId > 0) {
						const docTipoKey = baseTableHelpers.FindInBaseTableFromId(
							baseTipoDocList,
							anagrafica.docTipoId,
						).key;
						setShowDocTipoDetails(docTipoKey !== "tipo_documento_3");
					}
				}

				setAnagPep({
					IsNotPep: !anagrafica.isPep && !anagrafica.isNotPepMoreThanYear,
					IsPep: anagrafica.isPep && !anagrafica.isNotPepMoreThanYear,
					IsNotPepMoreThanYear: anagrafica.isNotPepMoreThanYear,
				});
				setCurrentAnagraficaOriginal(anagrafica);
				setCurrentAnagrafica(anagrafica);
				setShowAnagForm(true);
				loadAndCompareCervedDatas(anagrafica, baseProvinciaList, tipoAnag);
				handleModalResultClickClose();
			}
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleModalResultOkClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		}
	};
	//#endregion

	//#region Anchor El
	const open = Boolean(anchorEl);
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	//#endregion

	//#region EuroConference
	const handleEuroConfClick = async (itemId) => {
		try {
			setBackDropOpen(true);
			const openUrl = await euroConferenceApi.BuildEuroConferenceUrl(itemId);
			window.open(openUrl, "_blank", "noopener,noreferrer");
		} catch (e) {
			logApi.AddLog(
				e.message,
				e.stack,
				"HandleEuroConfClick",
				"AnagraficaForm",
			);
			enqueueSnackbar(t("error.generale"), { variant: "error" });
		} finally {
			setBackDropOpen(false);
		}
	};
	//#endregion

	//#region Return HTML
	return (
		<Fragment>
			<VaporPage.Section>
				<Loading open={backDropOpen} />

				{isDataLoaded && !userPermissions.canRead ? <NotAuth /> : null}

				<HistoryModal
					open={showHistoryModal}
					onClose={handleModalHistoryClose}
					onOk={handleModalHistoryOkClick}
					currentAnagrafica={currentAnagrafica}
					currentAnagraficaNewVer={currentAnagraficaNewVer}
					onChangeVersionDate={handleChangeVersionDate}
				/>

				<IdentModal
					open={showIdentModal}
					onClose={handleModalIdentClose}
					onOk={handleModalIdentOkClick}
				/>

				{showPrivateConfirmModal ? (
					<ModalPrivateAlert
						show={showPrivateConfirmModal}
						onClose={handlePrivateConfirmClose}
						onOk={handlePrivateConfirmOk}
						pendingPrivateValue={pendingPrivateValue}
						fascicoloName={fascicolo.name}
					/>
				) : null}

				{showDeleteModal && idToPerform > 0 && typeToPerform !== "" ? (
					<ModalDelete
						show={showDeleteModal}
						onClose={handleModalDeleteClose}
						onOk={handleModalDeleteOkClick}
						deleteMessage={t("modal.deleteDesc")}
					></ModalDelete>
				) : null}

				{showSaveConfirmModal && idToPerform > 0 && typeToPerform !== "" ? (
					<ModalConfirmSave
						show={showSaveConfirmModal}
						onClose={handleModalConfirmSaveClose}
						onOk={handleModalConfirmSaveOkClick}
					></ModalConfirmSave>
				) : null}

				{showSaveConfirmModalVer && idToPerform > 0 && typeToPerform !== "" ? (
					<ModalConfirmSave
						show={showSaveConfirmModalVer}
						onClose={handleModalConfirmSaveVerClose}
						onOk={handleModalConfirmSaveOkClickVer}
					></ModalConfirmSave>
				) : null}

				{showResultListModal && resultList.length > 0 && (
					<ModalSearchResultList
						show={showResultListModal}
						onClose={handleModalResultClickClose}
						onOk={handleModalResultOkClick}
						anagToShowList={resultList}
					></ModalSearchResultList>
				)}

				{isDataLoaded && userPermissions.canRead ? (
					<Fragment>
						<Stack
							direction="row"
							spacing={2}
							divider={<Divider orientation="vertical" flexItem />}
							sx={{ mt: 2 }}
						>
							<AnagraficaSidebar
								clientiList={currentAnagraficheClientiList}
								collegateList={currentAnagraficheCollegateList}
								titolariList={currentAnagraficheTitEffList}
								lastSelectedAnag={lastSelectedAnag}
								tipoAnag={tipoAnag}
								currentAnagraficaId={currentAnagrafica.id}
								allUtentiList={allUtentiList}
								isUserAllowedToPrivate={isUserAllowedToPrivate}
								fascicolo={fascicolo}
								userPermissions={userPermissions}
								getTipoAnagIdForDelete={getTipoAnagIdForDelete}
								onSelectAnagrafica={handleSelectedAnagClick}
								onDeleteAnagrafica={handleDeleteAnagClick}
								onAddAnagrafica={handleAddNewAnagrafica}
								onPrivateChange={handleChangeFascicolo}
							/>

							<Stack
								key={`anag-form-${currentAnagrafica.id}-${currentAnagrafica.versionN}`}
								direction="column"
								spacing={1}
								sx={{ width: "75%" }}
							>
								<AnagraficaVersionBar
									clienteAnagType={clienteAnagType}
									currentAnagrafica={currentAnagrafica}
									currentAnagraficaFascicolo={currentAnagraficaFascicolo}
									versioni={currentAnagraficheHistoryFilteredList}
									anchorEl={anchorEl}
									isCessato={isCessato}
									isNotLastVersion={isNotLastVersion}
									userPermissions={userPermissions}
									loggedUserId={loggedUserId}
									onMenuOpen={handleClick}
									onMenuClose={handleClose}
									onSelectVersion={handleSelectedAnagVerClick}
									onDeleteVersion={handleDeleteAnagVerClick}
								/>

								<AnagraficaSearchBar
									currentAnagrafica={currentAnagrafica}
									isPG={isPG}
									showAnagForm={showAnagForm}
									isCessato={isCessato}
									isNotLastVersion={isNotLastVersion}
									tipoAnag={tipoAnag}
									onChange={handleChange}
									onBlurCF={handleBlurCF}
									onBlurPIva={handleBlurPIva}
									onSearch={handleSearchClick}
                  					differences={differences}
								/>

								{showAnagForm ? (
									<Fragment>
										<Divider variant="standard" sx={{ pt: 1 }} />

										{(tipoAnag === "collegate" || tipoAnag === "titolare") && (
											<AnagraficaTipoSection
												currentAnagrafica={currentAnagrafica}
												currentAnagraficaFascicolo={currentAnagraficaFascicolo}
												baseTipoAnagList={baseTipoAnagList}
												baseTipoRappList={baseTipoRappList}
												tipoAnag={tipoAnag}
												enableTipoRappDesc={enableTipoRappDesc}
												isCessato={isCessato}
												isNotLastVersion={isNotLastVersion}
												onChange={handleChange}
												onCessDateChange={handleChangeCessDate}
											/>
										)}

										<AnagraficaIdentificazioneSection
											currentAnagrafica={currentAnagrafica}
											baseTipoIdentList={baseTipoIdentList}
											baseTipoDocList={baseTipoDocList}
											utentiList={utentiList}
											isModIdentArt22={isModIdentArt22}
											showDocTipoAp={showDocTipoAp}
											showDocTipoDetails={showDocTipoDetails}
											tipoAnag={tipoAnag}
											isPG={isPG}
											isCessato={isCessato}
											isNotLastVersion={isNotLastVersion}
											onChange={handleChange}
											onIdentDateChange={handleChangeIdentDate}
											onDocRilDateChange={handleChangeDocRilDate}
											onDocScadChange={handleChangeDocScad}
											onBlurDate={handleBlurDate}
											onEuroConfClick={handleEuroConfClick}
										/>

										<Divider variant="standard" sx={{ pt: 1 }} />

										<AnagraficaGeneralitaSection
											currentAnagrafica={currentAnagrafica}
											isPG={isPG}
											clienteAnagType={clienteAnagType}
											baseGenereList={baseGenereList}
											tenantTypeId={tenantTypeId}
											txtImportoRef={txtImportoRef}
											isCessato={isCessato}
											isNotLastVersion={isNotLastVersion}
											onChange={handleChange}
											onBlurPIva={handleBlurPIva}
											onDateChange={handleChangeDate}
											onImportoChange={handleChangeImporto}
											differences={differences}
										/>

										{isPG && (
											<AnagraficaAtecoSection
												currentAnagrafica={currentAnagrafica}
												baseAtecoList={baseAtecoList}
												isCessato={isCessato}
												isNotLastVersion={isNotLastVersion}
												onChange={handleChange}
											/>
										)}

										<AnagraficaIndirizziSection
											currentAnagrafica={currentAnagrafica}
											isPG={isPG}
											isAddrPaeseIta={isAddrPaeseIta}
											isAddr2PaeseIta={isAddr2PaeseIta}
											basePaeseList={basePaeseList}
											baseProvinciaList={baseProvinciaList}
											baseComuneList={baseComuneList}
											baseComuneList2={baseComuneList2}
											isCessato={isCessato}
											isNotLastVersion={isNotLastVersion}
											onChange={handleChange}
											onCopyAddress={handleCopyAddress}
                      differences={differences}
										/>

										{!isPG && (
											<Fragment>
												<AnagraficaPepSection
													currentAnagrafica={currentAnagrafica}
													anagPep={anagPep}
													basePoliticalRole={basePoliticalRole}
													isCessato={isCessato}
													isNotLastVersion={isNotLastVersion}
													onChange={handleChange}
												/>

												{tipoAnag === "titolare" && (
													<AnagraficaPartecipazioneSection
														currentAnagraficaFascicolo={
															currentAnagraficaFascicolo
														}
														currentAnagrafica={currentAnagrafica}
														baseTipoPartList={baseTipoPartList}
														showTipoPartDesc={showTipoPartDesc}
														loggedUserId={loggedUserId}
														isCessato={isCessato}
														isNotLastVersion={isNotLastVersion}
														onChange={handleChange}
													/>
												)}
											</Fragment>
										)}

										{tenantTypeId === 3 && tipoAnag === "cliente" && (
											<AnagraficaRedditoPatrimonioSection
												currentAnagrafica={currentAnagrafica}
												baseTipoReddito={baseTipoReddito}
												baseTipoPatrimonio={baseTipoPatrimonio}
												showOtherReddito={showOtherReddito}
												showOtherPatrimonio={showOtherPatrimonio}
												isCessato={isCessato}
												isNotLastVersion={isNotLastVersion}
												onChange={handleChange}
											/>
										)}
									</Fragment>
								) : null}
							</Stack>
						</Stack>
					</Fragment>
				) : null}
			</VaporPage.Section>

			{isDataLoaded && userPermissions.canCreate && showAnagForm ? (
				<VaporToolbar
					variant="regular"
					size="medium"
					withoutAppBar={false}
					contentRight={
						<Stack direction="row" spacing={2}>
							<Button
								variant="outlined"
								color="secondary"
								size="medium"
								endIcon={<CancelIcon />}
								onClick={handleCancelClick}
							>
								{t("actions.annulla")}
							</Button>
							{enableFormControls && (
								<Button
									variant="contained"
									size="medium"
									endIcon={<AddIcon />}
									onClick={checkSaveConfirmNeedVer}
									disabled={
										(currentAnagrafica.modifiedOn
											? isVersionSavingDisabled
											: currentAnagrafica.versionN <= 1) ||
										currentAnagraficaFascicolo.id === 0 ||
										currentAnagrafica.id === 0 ||
										isCessato ||
										isNotLastVersion
									}
								>
									{t("anagrafica.anagraficaForm.saveNewVer")}
								</Button>
							)}

							{(isCessato || !isNotLastVersion) && (
								<Button
									variant="contained"
									size="medium"
									endIcon={<SaveIcon />}
									onClick={checkSaveConfirmNeed}
									disabled={!enableFormControls}
								>
									{currentAnagrafica.id === 0 ||
									currentAnagraficaFascicolo.id === 0
										? t("actions.salva")
										: t("actions.aggiorna")}
								</Button>
							)}
						</Stack>
					}
				></VaporToolbar>
			) : null}
		</Fragment>
	);
	//#endregion
};
